import { API_BASE_URL, getApiBaseUrl } from '../../config';
import React, { useState, useEffect } from 'react';
import { getToken, getUser } from '../../utils/auth';
import usePermisos from '../../hooks/usePermisos';
import { 
  Modal, 
  TextField, 
  MenuItem, 
  LinearProgress, 
  Typography, 
  Box, 
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  Create as CreateIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import HistorialImportaciones from '../../components/HistorialImportaciones';
import GestionEmpresas from './GestionEmpresas';

const months = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const Recibos = () => {
  const { tienePermiso } = usePermisos();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Función helper global para formatear fechas de manera segura
  const formatFechaSegura = (fecha) => {
    if (!fecha) return '';
    
    // Si es un objeto Date, convertirlo usando métodos que eviten problemas de timezone
    if (fecha instanceof Date) {
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, '0');
      const day = String(fecha.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    
    // Si no es string, convertirlo a string
    if (typeof fecha !== 'string') {
      console.warn('formatFechaSegura recibió un valor no string:', fecha);
      fecha = String(fecha);
    }
    
    // Si la fecha viene en formato ISO con timezone, extraer solo la parte de fecha
    if (fecha.includes('T')) {
      const [y, m, d] = fecha.split('T')[0].split('-');
      return `${d}/${m}/${y}`;
    }
    
    // Si la fecha viene en formato YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [y, m, d] = fecha.split('-');
      return `${d}/${m}/${y}`;
    }
    
    // Si ya está en formato DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
      return fecha;
    }
    
    return fecha;
  };
  
  // Función para formatear números con punto de miles y coma decimal (formato argentino)
  const formatNumber = (num) => {
    if (!num || isNaN(num)) return '';
    const number = parseFloat(num);
    if (number === 0) return '';
    return number.toLocaleString('es-AR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Genera el HTML del recibo con leyenda y firma del empleado
  const generarHtmlReciboEmpleado = async (periodo, registros) => {
    const base = await generarHtmlRecibo(periodo, registros);
    // Datos del empleado y fecha/hora de la firma desde BD
    const nombre = registros[0]?.Nombre || '';
    const fechaFirmaDB = registros[0]?.fecha_firma;
    const fechaFirma = fechaFirmaDB ? new Date(fechaFirmaDB).toLocaleString('es-AR', { hour12: false }) : new Date().toLocaleString('es-AR', { hour12: false });
    
    // Obtener la URL base para construir rutas completas
    const baseUrl = getApiBaseUrl();
    const firmaUrl = registros[0]?.empresa_firma_url;
    let firmaBase64 = null;
    
    if (firmaUrl) {
      const fullFirmaUrl = firmaUrl.startsWith('http') ? firmaUrl : `${baseUrl}${firmaUrl}`;
      // Convertir imagen a base64 para embeberla en el HTML
      firmaBase64 = await imageToBase64(fullFirmaUrl);
    }

    // Formato nuevo (09/2026+): la firma está delimitada por marcadores;
    // en la copia del empleado va la conformidad y la firma del empleado
    // en vez de la del empleador.
    if (base.includes('<!--FIRMA_INICIO-->')) {
      const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return base.replace(
        /<!--FIRMA_INICIO-->[\s\S]*?<!--FIRMA_FIN-->/,
        `<div class="conformidad">RECIBÍ DE CONFORMIDAD EL IMPORTE DE LA PRESENTE LIQUIDACIÓN EN PAGO DE MI REMUNERACIÓN CORRESPONDIENTE AL PERÍODO ARRIBA INDICADO Y COPIA DE ESTE RECIBO</div>
    <div class="firma">
      <div style="font-weight:bold;">${esc(String(nombre).trim())}</div>
      <div style="font-size:6.5pt;margin-bottom:1mm;">Firmado digitalmente el ${esc(fechaFirma)}</div>
      <div class="linea">Firma del Empleado</div>
    </div>`
      );
    }

    return base
      .replace(
        /<div class="leyenda">[\s\S]*?<\/div>\s*<div class="firmas">[\s\S]*?<\/div>\s*<\/section>/,
        `<div class="leyenda">RECIBÍ DE CONFORMIDAD EL IMPORTE DE LA PRESENTE LIQUIDACIÓN EN PAGO DE MI REMUNERACIÓN CORRESPONDIENTE AL PERÍODO ARRIBA INDICADO Y COPIA DE ESTE RECIBO</div>
           <div class="firmas" style="display:flex;justify-content:flex-end;margin-top:20px;">
        <div class="sign no-line" style="margin-top:40px;text-align:center;margin-right:50px;">
          <div style="font-weight:bold;font-size:13px;">${nombre}</div>
          <div style="font-size:11px;color:#444;">Firmado digitalmente el ${fechaFirma}</div>
          <div style="width:220px;height:1px;border-bottom:1px solid #222;margin:16px auto 8px auto;"></div>
          <div style="margin-top:8px;">FIRMA DEL EMPLEADO</div>
        </div>
       </div>
    </section>`
      );
  };

  // Función para imprimir recibo firmado por el empleado
  const handleImprimirReciboEmpleado = async (claveGrupoOPeriodo) => {
    // Parsear la clave para obtener período, tipo y docNumero si existe
    let periodo, tipoLiquidacion, docNumero;
    
    if (claveGrupoOPeriodo.includes('_')) {
      const parsed = parseClaveGrupo(claveGrupoOPeriodo);
      periodo = parsed.periodo;
      tipoLiquidacion = parsed.tipoLiquidacion;
      docNumero = parsed.docNumero;
    } else {
      // Formato antiguo: solo período
      periodo = claveGrupoOPeriodo;
      tipoLiquidacion = null;
      docNumero = null;
    }
    
    const registros = (Array.isArray(recibosFiltrados) ? recibosFiltrados : []).filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      const coincideTipo = !tipoLiquidacion || (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      const coincideDoc = !docNumero || r.DocNumero === docNumero;
      return coincidePeriodo && coincideTipo && coincideDoc;
    });
    
    if (registros.length === 0) return;
    
    // Agrupar por legajo si hay múltiples
    const legajosUnicos = [...new Set(registros.map(r => r.Legajo).filter(Boolean))];
    
    if (legajosUnicos.length > 1) {
      // Generar recibos separados para cada legajo
      for (const legajo of legajosUnicos) {
        const registrosLegajo = registros.filter(r => r.Legajo === legajo);
        const printWindow = window.open('', '_blank');
        const reciboHtml = await generarHtmlReciboEmpleado(periodo, registrosLegajo);
        printWindow.document.write(reciboHtml);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 500 * legajosUnicos.indexOf(legajo));
        };
      }
    } else {
      // Lógica original para un solo legajo
      const printWindow = window.open('', '_blank');
      const reciboHtml = await generarHtmlReciboEmpleado(periodo, registros);
      printWindow.document.write(reciboHtml);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
    }
  };
  const user = getUser && getUser();
  const isAdmin = user && (user.rol === 'admin' || user.rol === 'superadmin' || user.rol === 'admin_rrhh');

  // Estados para consulta y agrupación
  const [recibos, setRecibos] = useState([]);
  const [agrupados, setAgrupados] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [openDetalle, setOpenDetalle] = useState(false);
  const [firma, setFirma] = useState({});
  const [firmaModal, setFirmaModal] = useState({ open: false, periodo: null });
  const [firmaPassword, setFirmaPassword] = useState('');
  const [firmaError, setFirmaError] = useState('');

  // Estados para el modal de importación
  const [openModal, setOpenModal] = useState(false);
  const [file, setFile] = useState(null);
  const [periodo, setPeriodo] = useState('');
  const [anio, setAnio] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [tipoLiquidacion, setTipoLiquidacion] = useState('mensual');
  const [empresaImportacion, setEmpresaImportacion] = useState('');
  const [empresasActivas, setEmpresasActivas] = useState([]);
  const [progress, setProgress] = useState(null);
  const [polling, setPolling] = useState(false);
  const [currentImportId, setCurrentImportId] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showEmpresas, setShowEmpresas] = useState(false);

  // Estados para la carga mensual de pago de aportes (nuevo formato de recibo)
  const [showPagosAportes, setShowPagosAportes] = useState(false);
  const [pagosAportes, setPagosAportes] = useState([]);
  const [pagoAportesEmpresa, setPagoAportesEmpresa] = useState('');
  const [pagoAportesMes, setPagoAportesMes] = useState('');
  const [pagoAportesAnio, setPagoAportesAnio] = useState('');
  const [pagoAportesFecha, setPagoAportesFecha] = useState('');
  const [pagoAportesBanco, setPagoAportesBanco] = useState('GALICIA');
  const [guardandoPagoAportes, setGuardandoPagoAportes] = useState(false);
  
  // Estados para manejo de empresas y legajos del usuario
  const [misLegajos, setMisLegajos] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('todas');
  const [legajoSeleccionado, setLegajoSeleccionado] = useState('');
  
  // Estados para búsqueda de superadmin
  const [busqueda, setBusqueda] = useState('');
  const [busquedaActiva, setBusquedaActiva] = useState('');
  
  // Estado para el modal de bienvenida/información
  const [mostrarModalInfo, setMostrarModalInfo] = useState(false);

  // Función helper para parsear claveGrupo
  // Formato: periodo_tipo_docnumero (superadmin) o periodo_tipo (usuarios normales)
  const parseClaveGrupo = (claveGrupo) => {
    const partes = claveGrupo.split('_');
    return {
      periodo: partes[0],
      tipoLiquidacion: partes[1] || 'mensual',
      docNumero: partes[2] || null  // Solo presente para superadmin
    };
  };

  // Mostrar modal de info al montar el componente (solo primera vez por sesión)
  useEffect(() => {
    const yaVioInfo = sessionStorage.getItem('vioInfoRecibos');
    if (!yaVioInfo) {
      setMostrarModalInfo(true);
      sessionStorage.setItem('vioInfoRecibos', 'true');
    }
  }, []);

  // Cargar empresas activas (para el selector de importación)
  const fetchEmpresasActivas = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/empresas/activas`, {
        headers: { Authorization: `Bearer ${getToken && getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmpresasActivas(data);
      }
    } catch (err) {
      console.error('Error al cargar empresas activas:', err);
    }
  };

  // Cargar la lista de pagos de aportes ya cargados (panel admin)
  const cargarPagosAportes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/pagos-aportes`, {
        headers: { Authorization: `Bearer ${getToken && getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPagosAportes(data);
      }
    } catch (err) {
      console.error('Error al cargar pagos de aportes:', err);
    }
  };

  const handleGuardarPagoAportes = async () => {
    if (!pagoAportesEmpresa || !pagoAportesMes || !pagoAportesAnio || !pagoAportesFecha) {
      alert('Completá empresa, mes, año y fecha de pago');
      return;
    }
    setGuardandoPagoAportes(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/pagos-aportes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken && getToken()}`
        },
        body: JSON.stringify({
          empresa_id: pagoAportesEmpresa,
          periodo: `${pagoAportesMes}/${pagoAportesAnio}`,
          fecha_pago: pagoAportesFecha,
          banco: pagoAportesBanco
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar');
      setPagoAportesMes('');
      setPagoAportesAnio('');
      setPagoAportesFecha('');
      await cargarPagosAportes();
    } catch (err) {
      alert(err.message || 'Error al guardar el pago de aportes');
    } finally {
      setGuardandoPagoAportes(false);
    }
  };

  const handleEliminarPagoAportes = async (id) => {
    if (!window.confirm('¿Eliminar este pago de aportes cargado?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/pagos-aportes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken && getToken()}` }
      });
      await cargarPagosAportes();
    } catch (err) {
      console.error('Error al eliminar pago de aportes:', err);
    }
  };

  // Cargar legajos del usuario
  const fetchMisLegajos = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/legajos/mis-legajos`, {
        headers: { Authorization: `Bearer ${getToken && getToken()}` }
      });
      if (res.ok) {
        const legajos = await res.json();
        setMisLegajos(legajos);
      }
    } catch (err) {
      console.error('Error cargando legajos:', err);
    }
  };

  useEffect(() => {
    const fetchRecibos = async () => {
      try {
        // Para superadmin, incluir parámetro de búsqueda
        const isSuperAdmin = user && user.rol === 'superadmin';
        let url = `${API_BASE_URL}/api/recibos/mis-recibos`;
        
        if (isSuperAdmin && busquedaActiva) {
          url += `?busqueda=${encodeURIComponent(busquedaActiva)}`;
        }
        
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getToken && getToken()}` }
        });
        const data = await res.json();
        setRecibos(data);
        
        // Inicializar estado de firmas con datos de la BD
        const firmasEstado = {};
        const agrupados = {};
        data.forEach(r => {
          // Crear clave única combinando período y tipo de liquidación
          const claveGrupo = `${r.PeriodoLiquidacion}_${r.tipo_liquidacion || 'mensual'}`;
          if (!agrupados[claveGrupo]) {
            agrupados[claveGrupo] = [];
            // Si cualquier recibo del período está firmado, considerar todo el período firmado
            firmasEstado[claveGrupo] = false;
          }
          agrupados[claveGrupo].push(r);
          // Si al menos un recibo está firmado, el período está firmado
          if (r.Firmado === 1) {
            firmasEstado[claveGrupo] = true;
          }
        });
        
        setFirma(firmasEstado);
        setAgrupados(Object.entries(agrupados));
      } catch (err) {
        setRecibos([]);
      }
    };
    
    fetchRecibos();
    fetchMisLegajos();
    if (isAdmin) {
      fetchEmpresasActivas();
    }
  }, [busquedaActiva]);

  // Prevenir gestos de swipe de navegación en móviles
  useEffect(() => {
    if (isMobile) {
      let startX = null;
      let startY = null;

      const handleTouchStart = (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      };

      const handleTouchMove = (e) => {
        if (!startX || !startY) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        
        const deltaX = Math.abs(currentX - startX);
        const deltaY = Math.abs(currentY - startY);
        
        // Solo prevenir si es un movimiento principalmente horizontal
        // y está cerca del borde de la pantalla (posible navegación)
        if (deltaX > deltaY && deltaX > 50) {
          const isLeftEdge = startX < 50; // Swipe desde borde izquierdo
          const isRightEdge = startX > window.innerWidth - 50; // Swipe desde borde derecho
          
          if (isLeftEdge || isRightEdge) {
            e.preventDefault();
          }
        }
      };

      const handleTouchEnd = () => {
        startX = null;
        startY = null;
      };

      // Solo aplicar estilos CSS mínimos necesarios
      const originalBodyStyle = {
        overscrollBehaviorX: document.body.style.overscrollBehaviorX
      };

      document.body.style.overscrollBehaviorX = 'none';

      // Agregar event listeners con passive: false solo para touchmove
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Cleanup al desmontar el componente
      return () => {
        // Restaurar estilos originales
        document.body.style.overscrollBehaviorX = originalBodyStyle.overscrollBehaviorX;

        // Remover event listeners
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isMobile]);

  // Filtrar recibos por empresa seleccionada
  const recibosFiltrados = React.useMemo(() => {
    if (empresaSeleccionada === 'todas' || !empresaSeleccionada) {
      return recibos;
    }
    
    // Obtener los legajos de la empresa seleccionada
    const legajosEmpresa = misLegajos
      .filter(l => l.empresa_id.toString() === empresaSeleccionada)
      .map(l => l.numero_legajo);
    
    return recibos.filter(r => legajosEmpresa.includes(r.Legajo));
  }, [recibos, empresaSeleccionada, misLegajos]);

  // Actualizar agrupados cuando cambien los recibos filtrados
  const agrupadosFiltrados = React.useMemo(() => {
    const agrupados = {};
    
    // Verificar si es superadmin
    const isSuperAdmin = user && user.rol === 'superadmin';
    
    recibosFiltrados.forEach(r => {
      // Para superadmin: agrupar por período + tipo + documento (cada empleado por separado)
      // Para usuarios normales: agrupar solo por período + tipo (todos sus recibos juntos)
      const claveGrupo = isSuperAdmin 
        ? `${r.PeriodoLiquidacion}_${r.tipo_liquidacion || 'mensual'}_${r.DocNumero}`
        : `${r.PeriodoLiquidacion}_${r.tipo_liquidacion || 'mensual'}`;
      
      if (!agrupados[claveGrupo]) {
        agrupados[claveGrupo] = [];
      }
      agrupados[claveGrupo].push(r);
    });
    
    return Object.entries(agrupados);
  }, [recibosFiltrados, user]);

  // Actualizar estado de firmas cuando cambien los recibos agrupados
  React.useEffect(() => {
    const firmasEstado = {};
    
    agrupadosFiltrados.forEach(([claveGrupo, registros]) => {
      firmasEstado[claveGrupo] = registros.some(r => r.Firmado === 1);
    });
    
    setFirma(firmasEstado);
  }, [agrupadosFiltrados]);

  const handleCloseDetalle = () => {
    setOpenDetalle(false);
    setDetalle(null);
  };

  // Función para imprimir recibo de una empresa específica
  const handleImprimirReciboPorEmpresa = async (claveGrupoOPeriodo, empresaId) => {
    // Parsear la clave para obtener período, tipo y docNumero si existe
    let periodo, tipoLiquidacion, docNumero;
    
    if (claveGrupoOPeriodo.includes('_')) {
      const parsed = parseClaveGrupo(claveGrupoOPeriodo);
      periodo = parsed.periodo;
      tipoLiquidacion = parsed.tipoLiquidacion;
      docNumero = parsed.docNumero;
    } else {
      periodo = claveGrupoOPeriodo;
      tipoLiquidacion = null;
      docNumero = null;
    }
    
    const registros = recibosFiltrados.filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      const coincideTipo = !tipoLiquidacion || (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      const coincideDoc = !docNumero || r.DocNumero === docNumero;
      return coincidePeriodo && coincideTipo && coincideDoc;
    });
    
    const legajosEmpresa = misLegajos
      .filter(l => l.empresa_id === empresaId)
      .map(l => l.numero_legajo);
    
    const registrosEmpresa = registros.filter(r => legajosEmpresa.includes(r.Legajo));
    
    if (registrosEmpresa.length === 0) return;

    // Obtener información de la empresa desde misLegajos
    let empresaInfo = misLegajos.find(l => l.empresa_id === empresaId);
    
    // Si no encuentra, intentar con conversión de tipos
    if (!empresaInfo) {
      empresaInfo = misLegajos.find(l => String(l.empresa_id) === String(empresaId));
    }
    
    const printWindow = window.open('', '_blank');
    const reciboHtml = await generarHtmlRecibo(periodo, registrosEmpresa, empresaInfo);
    printWindow.document.write(reciboHtml);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    };
  };

  // Función para verificar si se puede ver un recibo
  const puedeVerRecibo = (claveGrupo) => {
    // Ordenar períodos de más antiguo a más nuevo
    const periodosOrdenados = agrupadosFiltrados
      .map(([clave]) => clave)
      .sort((a, b) => {
        // Extraer solo el período para la comparación
        const [periodoA] = a.split('_');
        const [periodoB] = b.split('_');
        const [mesA, anioA] = periodoA.split('/').map(Number);
        const [mesB, anioB] = periodoB.split('/').map(Number);
        return anioA !== anioB ? anioA - anioB : mesA - mesB;
      });

    const indicePeriodo = periodosOrdenados.indexOf(claveGrupo);
    
    // Si es el primer período o todos los anteriores están firmados
    if (indicePeriodo === 0) return true;
    
    // Verificar que todos los períodos anteriores estén firmados
    for (let i = 0; i < indicePeriodo; i++) {
      if (!firma[periodosOrdenados[i]]) {
        return false;
      }
    }
    
    return true;
  };

  const handleVerDetalle = (claveGrupo) => {
    if (!puedeVerRecibo(claveGrupo)) {
      alert('Para ver este recibo, primero debe firmar todos los recibos anteriores.');
      return;
    }
    
    // Extraer período, tipo de liquidación y docNumero si existe
    const { periodo, tipoLiquidacion, docNumero } = parseClaveGrupo(claveGrupo);
    const registros = recibosFiltrados.filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      const coincideTipo = (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      const coincideDoc = !docNumero || r.DocNumero === docNumero;
      return coincidePeriodo && coincideTipo && coincideDoc;
    });
    setDetalle({ periodo: `${periodo} (${tipoLiquidacion === 'sac' ? 'SAC' : 'Mensual'})`, registros });
    setOpenDetalle(true);
  };

  // Abrir modal de firma
  const handleFirmar = (claveGrupo) => {
    setFirmaModal({ open: true, periodo: claveGrupo });
    setFirmaPassword('');
    setFirmaError('');
  };

  // Enviar firma al backend
  const handleFirmaSubmit = async () => {
    if (!firmaPassword) {
      setFirmaError('Ingresá tu contraseña');
      return;
    }
    
    try {
      // Extraer período, tipo de liquidación y docNumero si existe
      const { periodo, tipoLiquidacion, docNumero } = parseClaveGrupo(firmaModal.periodo);
      
      // Obtener todos los recibos del período para este usuario
      const recibosDelPeriodo = recibosFiltrados.filter(r => {
        const coincidePeriodo = r.PeriodoLiquidacion === periodo;
        const coincideTipo = (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
        const coincideDoc = !docNumero || r.DocNumero === docNumero;
        return coincidePeriodo && coincideTipo && coincideDoc;
      });
      const legajosUnicos = [...new Set(recibosDelPeriodo.map(r => r.Legajo).filter(Boolean))];
      
      // Si hay múltiples legajos, firmar cada uno por separado
      if (legajosUnicos.length > 1) {
        let todasLasFirmasExitosas = true;
        
        for (const legajo of legajosUnicos) {
          const res = await fetch(`${API_BASE_URL}/api/recibos/firmar`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getToken && getToken()}`
            },
            body: JSON.stringify({ 
              periodo: periodo, // Usar solo el período para el backend 
              password: firmaPassword,
              legajo: legajo
            })
          });
          
          const data = await res.json();
          if (!res.ok || !data.success) {
            todasLasFirmasExitosas = false;
            setFirmaError(data.error || `Error al firmar recibo del legajo ${legajo}`);
            break;
          }
        }
        
        if (todasLasFirmasExitosas) {
          // Actualizar el estado de recibos marcando todos los legajos del período como firmados
          setRecibos(prev => prev.map(r => {
            const coincidePeriodo = r.PeriodoLiquidacion === periodo;
            const coincideTipo = (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
            const coincideDoc = !docNumero || r.DocNumero === docNumero;
            if (coincidePeriodo && coincideTipo && coincideDoc) {
              return { ...r, Firmado: 1 };
            }
            return r;
          }));
          setFirmaModal({ open: false, periodo: null });
        }
      } else {
        // Lógica original para un solo legajo
        const res = await fetch(`${API_BASE_URL}/api/recibos/firmar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken && getToken()}`
          },
          body: JSON.stringify({ 
            periodo: periodo, // Usar solo el período para el backend
            password: firmaPassword,
            legajo: legajosUnicos[0] || null
          })
        });
        
        const data = await res.json();
        if (res.ok && data.success) {
          // Actualizar el estado de recibos marcando los registros firmados
          setRecibos(prev => prev.map(r => {
            const coincidePeriodo = r.PeriodoLiquidacion === periodo;
            const coincideTipo = (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
            const coincideDoc = !docNumero || r.DocNumero === docNumero;
            const coincideLegajo = !legajosUnicos[0] || r.Legajo === legajosUnicos[0];
            if (coincidePeriodo && coincideTipo && coincideDoc && coincideLegajo) {
              return { ...r, Firmado: 1 };
            }
            return r;
          }));
          setFirmaModal({ open: false, periodo: null });
        } else {
          setFirmaError(data.error || 'Contraseña incorrecta');
        }
      }
    } catch (err) {
      console.error('Error al firmar:', err);
      setFirmaError('Error al firmar el recibo');
    }
  };

  // Función para imprimir/descargar PDF
  const handleImprimirRecibo = async (claveGrupoOPeriodo) => {
    // Parsear la clave para obtener período, tipo y docNumero si existe
    let periodo, tipoLiquidacion, docNumero;
    
    if (claveGrupoOPeriodo.includes('_')) {
      const parsed = parseClaveGrupo(claveGrupoOPeriodo);
      periodo = parsed.periodo;
      tipoLiquidacion = parsed.tipoLiquidacion;
      docNumero = parsed.docNumero;
    } else {
      periodo = claveGrupoOPeriodo;
      tipoLiquidacion = null;
      docNumero = null;
    }
    
    const registros = recibosFiltrados.filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      const coincideTipo = !tipoLiquidacion || (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      const coincideDoc = !docNumero || r.DocNumero === docNumero;
      return coincidePeriodo && coincideTipo && coincideDoc;
    });
    
    if (registros.length === 0) return;

    // Agrupar por empresa y legajo
    const empresasUnicas = [...new Set(registros.map(r => r.empresa_nombre || 'Sin Empresa'))];
    
    if (empresasUnicas.length > 1) {
      // Generar recibos separados por empresa
      const promises = empresasUnicas.map(async (empresaNombre, index) => {
        const registrosEmpresa = registros.filter(r => 
          (r.empresa_nombre || 'Sin Empresa') === empresaNombre
        );
        
        const reciboHtml = await generarHtmlRecibo(periodo, registrosEmpresa);
        
        return { html: reciboHtml, index };
      });
      
      const resultados = await Promise.all(promises);
      
      resultados.forEach(({ html, index }) => {
        setTimeout(() => {
          const printWindow = window.open('', '_blank');
          printWindow.document.write(html);
          printWindow.document.close();
          
          // Esperar a que cargue y luego imprimir
          printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
          };
        }, 500 * index); // Retrasar cada impresión para evitar conflictos
      });
    } else {
      // Una sola empresa o sin empresa
      const printWindow = window.open('', '_blank');
      const reciboHtml = await generarHtmlRecibo(periodo, registros);
      
      printWindow.document.write(reciboHtml);
      printWindow.document.close();
      
      // Esperar a que cargue y luego imprimir
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        // Cerrar la ventana después de imprimir (opcional)
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
    }
  };

  // Función para ver PDF sin imprimir automáticamente
  const handleVerPdf = async (claveGrupoOPeriodo) => {
    // Si viene con _, es una clave compuesta; si no, es solo período (llamadas antiguas)
    let periodo, tipoLiquidacion;
    if (claveGrupoOPeriodo.includes('_')) {
      [periodo, tipoLiquidacion] = claveGrupoOPeriodo.split('_');
    } else {
      periodo = claveGrupoOPeriodo;
      tipoLiquidacion = null; // No filtrar por tipo
    }
    
    const registros = recibosFiltrados.filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      if (tipoLiquidacion) {
        return coincidePeriodo && (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      }
      return coincidePeriodo;
    });
    
    if (registros.length === 0) return;

    // Agrupar por empresa y legajo
    const empresasUnicas = [...new Set(registros.map(r => r.empresa_nombre || 'Sin Empresa'))];
    
    if (empresasUnicas.length > 1) {
      // Generar recibos separados por empresa
      empresasUnicas.forEach(async (empresaNombre, index) => {
        const registrosEmpresa = registros.filter(r => 
          (r.empresa_nombre || 'Sin Empresa') === empresaNombre
        );
        
        const reciboHtml = await generarHtmlRecibo(periodo, registrosEmpresa);
        
        setTimeout(() => {
          const pdfWindow = window.open('', '_blank');
          pdfWindow.document.write(reciboHtml);
          pdfWindow.document.close();
          
          // Solo enfocar la ventana, sin imprimir
          pdfWindow.onload = () => {
            pdfWindow.focus();
          };
        }, 100 * index); // Menor retraso porque no imprimimos
      });
    } else {
      // Una sola empresa o sin empresa
      const pdfWindow = window.open('', '_blank');
      const reciboHtml = await generarHtmlRecibo(periodo, registros);
      
      pdfWindow.document.write(reciboHtml);
      pdfWindow.document.close();
      
      // Solo enfocar la ventana, sin imprimir
      pdfWindow.onload = () => {
        pdfWindow.focus();
      };
    }
  };

  // Función para ver PDF del empleado sin imprimir automáticamente
  const handleVerPdfEmpleado = async (claveGrupoOPeriodo) => {
    // Si viene con _, es una clave compuesta; si no, es solo período (llamadas antiguas)
    let periodo, tipoLiquidacion;
    if (claveGrupoOPeriodo.includes('_')) {
      [periodo, tipoLiquidacion] = claveGrupoOPeriodo.split('_');
    } else {
      periodo = claveGrupoOPeriodo;
      tipoLiquidacion = null; // No filtrar por tipo
    }
    
    const registros = recibosFiltrados.filter(r => {
      const coincidePeriodo = r.PeriodoLiquidacion === periodo;
      if (tipoLiquidacion) {
        return coincidePeriodo && (r.tipo_liquidacion || 'mensual') === tipoLiquidacion;
      }
      return coincidePeriodo;
    });
    
    if (registros.length === 0) return;

    try {
      // Usar la misma lógica que handleImprimirReciboEmpleado pero sin imprimir
      const legajosUnicos = [...new Set(registros.map(r => r.Legajo).filter(Boolean))];

      if (legajosUnicos.length > 1) {
        // Abrir un PDF por cada legajo
        for (const legajo of legajosUnicos) {
          const registrosLegajo = registros.filter(r => r.Legajo === legajo);
          const reciboHtml = await generarHtmlReciboEmpleado(periodo, registrosLegajo);
          const pdfWindow = window.open('', '_blank');
          pdfWindow.document.write(reciboHtml);
          pdfWindow.document.close();
          pdfWindow.onload = () => {
            pdfWindow.focus();
          };
        }
      } else {
        const reciboHtml = await generarHtmlReciboEmpleado(periodo, registros);
        const pdfWindow = window.open('', '_blank');
        pdfWindow.document.write(reciboHtml);
        pdfWindow.document.close();
        pdfWindow.onload = () => {
          pdfWindow.focus();
        };
      }

    } catch (error) {
      console.error('Error:', error);
      alert('Error al generar el recibo del empleado: ' + error.message);
    }
  };

  // Función específica para formatear fechas de ingreso y antigüedad que vienen de la BD
  const formatearFechaBD = (fecha) => {
    if (!fecha) return '';
    
    // Si es un objeto Date, extraer componentes sin conversión de timezone
    if (fecha instanceof Date) {
      // Método alternativo: obtener los componentes directamente desde el string
      const fechaString = fecha.toString();
      
      // Intentar obtener la fecha original sin timezone
      const year = fecha.getFullYear();
      const month = fecha.getMonth() + 1; // getMonth() devuelve 0-11
      const day = fecha.getDate();
      
      // Verificar si hay diferencia con UTC (indicador de problema de timezone)
      const utcYear = fecha.getUTCFullYear();
      const utcMonth = fecha.getUTCMonth() + 1;
      const utcDay = fecha.getUTCDate();
      
      // Si hay diferencia entre local y UTC, usar UTC
      let finalDay, finalMonth, finalYear;
      if (day !== utcDay || month !== utcMonth || year !== utcYear) {
        finalDay = utcDay;
        finalMonth = utcMonth;
        finalYear = utcYear;
      } else {
        finalDay = day;
        finalMonth = month;
        finalYear = year;
      }
      
      const fechaFormateada = `${String(finalDay).padStart(2, '0')}/${String(finalMonth).padStart(2, '0')}/${finalYear}`;
      return fechaFormateada;
    }
    
    // Para strings, procesar directamente
    let fechaStr = String(fecha);
    
    // Si la fecha viene en formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ o similar)
    if (fechaStr.includes('T')) {
      const [y, m, d] = fechaStr.split('T')[0].split('-');
      return `${d}/${m}/${y}`;
    }
    
    // Si la fecha viene en formato YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
      const [y, m, d] = fechaStr.split('-');
      const fechaFormateada = `${d}/${m}/${y}`;
      return fechaFormateada;
    }
    
    // Si ya está en formato DD/MM/YYYY o similar
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(fechaStr)) {
      return fechaStr;
    }
    
    return fechaStr;
  };

  // Función para convertir números a letras
  const numeroALetras = (numero) => {
    const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
    const decenas = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
    const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

    if (numero === 0) return 'cero';
    if (numero === 100) return 'cien';
    if (numero === 1000) return 'mil';
    if (numero === 1000000) return 'un millón';

    const convertirGrupo = (n) => {
      let resultado = '';
      const c = Math.floor(n / 100);
      const d = Math.floor((n % 100) / 10);
      const u = n % 10;

      if (c > 0) {
        if (c === 1 && d === 0 && u === 0) {
          resultado += 'cien';
        } else {
          resultado += centenas[c];
        }
      }

      if (d >= 2) {
        if (resultado) resultado += ' ';
        resultado += decenas[d];
        if (u > 0) {
          resultado += ' y ' + unidades[u];
        }
      } else if (d === 1) {
        if (resultado) resultado += ' ';
        resultado += especiales[u];
      } else if (u > 0) {
        if (resultado) resultado += ' ';
        resultado += unidades[u];
      }

      return resultado;
    };

    let resultado = '';
    const partes = [];
    
    // Millones
    if (numero >= 1000000) {
      const millones = Math.floor(numero / 1000000);
      if (millones === 1) {
        partes.push('un millón');
      } else {
        partes.push(convertirGrupo(millones) + ' millones');
      }
      numero %= 1000000;
    }

    // Miles
    if (numero >= 1000) {
      const miles = Math.floor(numero / 1000);
      if (miles === 1) {
        partes.push('mil');
      } else {
        partes.push(convertirGrupo(miles) + ' mil');
      }
      numero %= 1000;
    }

    // Unidades
    if (numero > 0) {
      partes.push(convertirGrupo(numero));
    }

    resultado = partes.join(' ');
    return resultado.charAt(0).toUpperCase() + resultado.slice(1);
  };

  // Función para convertir imagen a base64
  const imageToBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        console.error('Error fetching image:', response.status, response.statusText);
        return null;
      }
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  // Función para generar HTML del recibo para impresión
  const generarHtmlRecibo = async (periodo, registros, empresaInfo = null) => {
    // Obtener la URL base para construir rutas completas (detección dinámica de IP)
    const baseUrl = getApiBaseUrl();
    
    const firmaUrl = registros[0]?.empresa_firma_url;
    let firmaBase64 = null;
    
    if (firmaUrl) {
      const fullFirmaUrl = firmaUrl.startsWith('http') ? firmaUrl : `${baseUrl}${firmaUrl}`;
      
      // Convertir imagen a base64 para embeberla en el HTML
      firmaBase64 = await imageToBase64(fullFirmaUrl);
    }
    
    const logoUrl = registros[0]?.empresa_logo_url;
    let logoBase64 = null;
    
    if (logoUrl) {
      const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${baseUrl}${logoUrl}`;
      
      // Convertir imagen a base64 para embeberla en el HTML
      logoBase64 = await imageToBase64(fullLogoUrl);
    }
    
    // --- PLANTILLA EXACTA DEL USUARIO ---
    // 1. Preparar datos y helpers
    const formatFecha = (fecha) => {
      if (!fecha) return '';
      
      // Si es un objeto Date, convertirlo usando métodos que eviten problemas de timezone
      if (fecha instanceof Date) {
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        return `${day}/${month}/${year}`;
      }
      
      // Si no es string, convertirlo a string
      if (typeof fecha !== 'string') {
        console.warn('formatFecha recibió un valor no string:', fecha);
        fecha = String(fecha);
      }
      
      // Si la fecha viene en formato ISO con timezone, extraer solo la parte de fecha
      if (fecha.includes('T')) {
        const [y, m, d] = fecha.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
      }
      
      // Si la fecha viene en formato YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        const [y, m, d] = fecha.split('-');
        return `${d}/${m}/${y}`;
      }
      
      // Si ya está en formato DD/MM/YYYY
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
        return fecha;
      }
      
      return fecha;
    };
    // El PDF de referencia del formato nuevo usa año de 2 dígitos en
    // "Fecha Pago" y "F. Pago Aportes" (ej. 30/09/26), a diferencia del
    // resto del recibo.
    const formatFechaCorta = (fecha) => {
      const formateada = formatFecha(fecha);
      const partes = formateada.split('/');
      if (partes.length === 3 && partes[2].length === 4) {
        return `${partes[0]}/${partes[1]}/${partes[2].slice(-2)}`;
      }
      return formateada;
    };
    const conceptos = registros.filter(r => {
      const habCRet = parseFloat(r.ConcImpHabCRet) || 0;
      const habSRet = parseFloat(r.ConcImpHabSRet) || 0;
      const ret = parseFloat(r.ConcImpRet) || 0;
      return habCRet !== 0 || habSRet !== 0 || ret !== 0;
    }).sort((a, b) => {
      // Ordenar conceptos: primero haberes c/ret, luego haberes s/ret, luego retenciones
      const habCRetA = parseFloat(a.ConcImpHabCRet) || 0;
      const habSRetA = parseFloat(a.ConcImpHabSRet) || 0;
      const retA = parseFloat(a.ConcImpRet) || 0;
      
      const habCRetB = parseFloat(b.ConcImpHabCRet) || 0;
      const habSRetB = parseFloat(b.ConcImpHabSRet) || 0;
      const retB = parseFloat(b.ConcImpRet) || 0;
      
      // Determinar el tipo de concepto para A
      let tipoA = 3; // Por defecto, otros
      if (habCRetA > 0) tipoA = 1; // Haberes c/ret
      else if (habSRetA > 0) tipoA = 2; // Haberes s/ret
      else if (retA > 0) tipoA = 3; // Retenciones
      
      // Determinar el tipo de concepto para B
      let tipoB = 3; // Por defecto, otros
      if (habCRetB > 0) tipoB = 1; // Haberes c/ret
      else if (habSRetB > 0) tipoB = 2; // Haberes s/ret
      else if (retB > 0) tipoB = 3; // Retenciones
      
      if (tipoA !== tipoB) return tipoA - tipoB;
      // Dentro del mismo tipo, poner Basico siempre primero
      const esBasicoA = a.ConcDescr && /basico|b\u00e1sico/i.test(a.ConcDescr);
      const esBasicoB = b.ConcDescr && /basico|b\u00e1sico/i.test(b.ConcDescr);
      if (esBasicoA && !esBasicoB) return -1;
      if (!esBasicoA && esBasicoB) return 1;
      return 0;
    });
    // 2. Rellenar filas en blanco hasta 20 filas
    const MAX_FILAS = 20;
    const filasConceptos = [];
    for (let i = 0; i < MAX_FILAS; i++) {
      if (i < conceptos.length) {
        const r = conceptos[i];
        filasConceptos.push(`
          <tr><td>${r.ConcDescr || ''}</td><td class="num">${r.ConcCant || ''}</td><td class="num">${formatNumber(r.UnCant)}</td><td class="num">${formatNumber(r.ConcImpHabCRet)}</td><td class="num">${formatNumber(r.ConcImpHabSRet)}</td><td class="num">${formatNumber(r.ConcImpRet)}</td></tr>
        `.trim());
      } else {
        filasConceptos.push('<tr><td>&nbsp;</td><td class="num"></td><td class="num"></td><td class="num"></td><td class="num"></td><td class="num"></td></tr>');
      }
    }
    // 3. Totales
    const subtotalHabCRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabCRet) || 0), 0);
    const subtotalHabSRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabSRet) || 0), 0);
    const subtotalRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpRet) || 0), 0);
    const neto = subtotalHabCRet + subtotalHabSRet - subtotalRet;
    
    // Obtener el valor del sueldo/jornal: buscar específicamente el concepto Básico
    const conceptoBasicoSJ = conceptos.find(c => c.ConcDescr && /basico|b\u00e1sico/i.test(c.ConcDescr) && parseFloat(c.ConcImpHabCRet) > 0);
    const primerConceptoHabCRet = conceptos.find(c => parseFloat(c.ConcImpHabCRet) > 0);
    const sueldoJornal = conceptoBasicoSJ ? parseFloat(conceptoBasicoSJ.ConcImpHabCRet) : (primerConceptoHabCRet ? parseFloat(primerConceptoHabCRet.ConcImpHabCRet) : 0);

    // 3b. Formato nuevo de recibo (vigente desde el período 09/2026, todas
    // las empresas). Replica el recibo de referencia de la liquidación
    // (rnf1.pdf / rnfdev.pdf). Los períodos anteriores siguen con la
    // plantilla de siempre, más abajo.
    const [periodoMes, periodoAnio] = String(registros[0]?.PeriodoLiquidacion || '').split('/').map(n => parseInt(n, 10));
    const esFormatoNuevo = periodoAnio > 2026 || (periodoAnio === 2026 && periodoMes >= 9);

    // 4. Render HTML exacto
    const nombreEmpresa = empresaInfo?.empresa_nombre || registros[0]?.empresa_nombre || registros[0]?.empresa_razon_social || 'Compañía Integral de Alimentos SA';
    const direccionEmpresa = empresaInfo?.empresa_direccion || registros[0]?.empresa_direccion || 'Andrés Rolón 681, San Isidro. CP 1642. Buenos Aires';
    const cuitEmpresa = empresaInfo?.empresa_cuit || registros[0]?.empresa_cuit || '33-58648427-9';

    if (esFormatoNuevo) {
      const r0 = registros[0] || {};
      const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
      const fmt = (v) => num(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const txt = (t) => esc(String(t ?? '').trim());
      const espacios = (t) => esc(t).replace(/ /g, '&nbsp;');
      const cuilConGuiones = (c) => {
        const d = String(c ?? '').replace(/\D/g, '');
        return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : String(c ?? '').trim();
      };
      const legajoTexto = (l) => {
        const v = String(l ?? '').trim();
        return /^\d+$/.test(v) ? v.padStart(6, '0') : v;
      };

      // Depósito de aportes del período anterior (en el recibo de 09/26 se
      // informa el de 08/26): fecha y banco se cargan a mano mes a mes
      // desde "Cargar Pago de Aportes".
      const mesAnteriorDate = new Date(periodoAnio, periodoMes - 2, 1);
      const mesAnteriorMM = String(mesAnteriorDate.getMonth() + 1).padStart(2, '0');
      const mesAnteriorYYYY = mesAnteriorDate.getFullYear();
      let fechaPagoAportes = '';
      let bancoAportes = '';
      try {
        if (r0.empresa_id) {
          const resp = await fetch(`${baseUrl}/api/pagos-aportes/consultar?empresa_id=${r0.empresa_id}&periodo=${mesAnteriorMM}/${mesAnteriorYYYY}`, {
            headers: { Authorization: `Bearer ${getToken && getToken()}` }
          });
          if (resp.ok) {
            const dataAportes = await resp.json();
            if (dataAportes?.fecha_pago) {
              fechaPagoAportes = formatFechaCorta(dataAportes.fecha_pago);
              bancoAportes = dataAportes.banco || '';
            }
          }
        }
      } catch (e) {
        console.error('Error al consultar pago de aportes:', e);
      }
      const fPagoAportes = `${mesAnteriorMM}/${String(mesAnteriorYYYY).slice(-2)}${fechaPagoAportes ? ' ' + fechaPagoAportes : ''}`;

      // Antigüedad al día de pago, siempre en plural ("22 Años 1 Meses")
      const antiguedad = (() => {
        const desde = r0.FecBaseAnt || r0.FecIngreso;
        if (!desde) return '';
        const inicio = new Date(desde);
        const ref = r0.FechaPago ? new Date(r0.FechaPago) : new Date();
        if (isNaN(inicio.getTime()) || isNaN(ref.getTime())) return '';
        let anios = ref.getUTCFullYear() - inicio.getUTCFullYear();
        let meses = ref.getUTCMonth() - inicio.getUTCMonth();
        if (ref.getUTCDate() < inicio.getUTCDate()) meses--;
        if (meses < 0) { anios--; meses += 12; }
        return anios < 0 ? '' : `${anios} Años ${meses} Meses`;
      })();

      const tipoLiq = { mensual: 'MEN', sac: 'SAC' }[r0.tipo_liquidacion || 'mensual'] || String(r0.tipo_liquidacion || '').slice(0, 3).toUpperCase();
      const liquidacion = `${tipoLiq}&nbsp;&nbsp;${String(periodoMes).padStart(2, '0')}/${String(periodoAnio).slice(-2)}`;

      // Conceptos en el orden en que vienen en el Excel de la liquidación.
      // Unidad: cantidad, con "%" cuando UnCant viene vacío (así distingue
      // el Excel los conceptos porcentuales: "  " = unidades, "" = %).
      // Base = monto / cantidad (o / porcentaje); si no hay cantidad, la
      // base es el mismo monto. Verificado contra rnfdev.pdf.
      const conceptosOrdenados = [...conceptos].sort((a, b) => (num(a.id) - num(b.id)));
      const filasConceptos = conceptosOrdenados.map(r => {
        const monto = num(r.ConcImpHabCRet) || num(r.ConcImpHabSRet) || num(r.ConcImpRet);
        const cant = num(r.ConcCant);
        const esPorcentaje = r.UnCant === null || r.UnCant === undefined || r.UnCant === '';
        let unidad = '';
        let base = monto;
        if (cant) {
          unidad = esPorcentaje ? `${fmt(cant)}%` : fmt(cant);
          base = esPorcentaje ? monto / (cant / 100) : monto / cant;
        }
        let descripcion = String(r.ConcDescr || '').trim();
        if (/^obra social$/i.test(descripcion) && r.DescOS) {
          descripcion = `Obra Social ${String(r.DescOS).trim()}`;
        }
        const clase = String(r.ConcClaseLetras || '').trim();
        const nro = String(r.ConcNro || '').trim().padEnd(4, ' ');
        return `<tr><td class="c-desc">${espacios(`${clase} ${nro}  `)}${esc(descripcion)}</td><td class="c-num">${unidad}</td><td class="c-num">${fmt(base)}</td><td class="c-num">${fmt(monto)}</td></tr>`;
      }).join('');

      // Detalle de la composición salarial (AcumuladorReg6..18)
      const a = (n) => num(r0[`AcumuladorReg${n}`]);
      const sindicalEmp = a(6), sindicalTra = a(7);
      const segSocialEmp = a(8), segSocialTra = a(9);
      const obraSocialEmp = a(10), obraSocialTra = a(11);
      const inssjpEmp = a(12), inssjpTra = a(13);
      const artEmp = a(14), scvoEmp = a(15);
      const otrosEmp = a(16), otrosTra = a(17);
      const netoAcum = a(18);
      const totalSindical = sindicalEmp + sindicalTra;
      const totalSegSocial = segSocialEmp + segSocialTra;
      const totalObraSocial = obraSocialEmp + obraSocialTra;
      const totalInssjp = inssjpEmp + inssjpTra;
      const totalOtros = otrosEmp + otrosTra;

      const grupo = (titulo, total, filas) => `
        <div class="g">
          <div class="g-fila g-tot"><span>${titulo}</span><span>${fmt(total)}</span></div>
          ${filas.map(([etq, val]) => `<div class="g-fila"><span>${etq}</span><span>${fmt(val)}</span></div>`).join('')}
        </div>`;

      // "Costo Total Empleador" = totales por categoría + Neto. Reproduce
      // exactamente los porcentajes de los dos PDF de referencia.
      const costoTotal = totalSindical + totalSegSocial + totalObraSocial + totalInssjp + artEmp + scvoEmp + totalOtros + netoAcum;
      const porcentaje = (v) => (costoTotal > 0 ? (v / costoTotal) * 100 : 0).toFixed(2).replace('.', ',') + '%';

      // Orden y colores de las porciones tal cual el gráfico de referencia
      // (sentido horario desde las 12)
      const porciones = [
        { label: 'ART', value: artEmp, color: '#0000FF' },
        { label: 'INSSJP', value: totalInssjp, color: '#FF0000' },
        { label: 'Costo Sindical', value: totalSindical, color: '#FFFF00' },
        { label: 'Obra Social', value: totalObraSocial, color: '#33FF33' },
        { label: 'Seguridad Social', value: totalSegSocial, color: '#FF00FF' },
        { label: 'SCVO', value: scvoEmp, color: '#C0C0C0' },
        { label: 'Otros', value: totalOtros, color: '#00FFFF' },
        { label: 'Neto', value: netoAcum, color: '#33BB00' }
      ];
      const colorDe = (label) => porciones.find(p => p.label === label).color;
      const leyendaItem = (label) => label
        ? `<div class="ley-item"><span class="ley-color" style="background:${colorDe(label)}"></span>${label}</div>`
        : '<div></div>';
      const leyenda = ['Costo Sindical', 'Obra Social', 'ART', 'Seguridad Social', 'INSSJP', 'SCVO', 'Otros', '', 'Neto'].map(leyendaItem).join('');

      // Torta 3D (en mm, dentro de un SVG de 62 x 47 mm)
      const tortaSvg = (() => {
        const cx = 29.6, cy = 19.8, rx = 20.4, ry = 16.5, h = 4.2;
        const oscurecer = (hex) => {
          const n = parseInt(hex.slice(1), 16);
          const c = (v) => Math.round(v * 0.6).toString(16).padStart(2, '0');
          return `#${c((n >> 16) & 255)}${c((n >> 8) & 255)}${c(n & 255)}`;
        };
        const P = (t, dy = 0) => [cx + rx * Math.sin(t), cy - ry * Math.cos(t) + dy];
        const pt = ([x, y]) => `${x.toFixed(3)} ${y.toFixed(3)}`;
        const visibles = porciones.filter(p => p.value > 0);
        if (costoTotal <= 0 || visibles.length === 0) return '';
        let t = 0;
        const arcos = visibles.map(p => {
          const ini = t;
          t += (p.value / costoTotal) * 2 * Math.PI;
          return { ...p, ini, fin: t };
        });
        const lados = [];
        const caras = [];
        const etiquetas = [];
        if (arcos.length === 1) {
          const p = arcos[0];
          lados.push(`<path d="M ${pt(P(Math.PI / 2))} A ${rx} ${ry} 0 0 1 ${pt(P(3 * Math.PI / 2))} L ${pt(P(3 * Math.PI / 2, h))} A ${rx} ${ry} 0 0 0 ${pt(P(Math.PI / 2, h))} Z" fill="${oscurecer(p.color)}"/>`);
          caras.push(`<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${p.color}"/>`);
        } else {
          arcos.forEach(p => {
            const s = Math.max(p.ini, Math.PI / 2);
            const e = Math.min(p.fin, 3 * Math.PI / 2);
            if (e > s) {
              lados.push(`<path d="M ${pt(P(s))} A ${rx} ${ry} 0 0 1 ${pt(P(e))} L ${pt(P(e, h))} A ${rx} ${ry} 0 0 0 ${pt(P(s, h))} Z" fill="${oscurecer(p.color)}"/>`);
            }
            const grande = (p.fin - p.ini) > Math.PI ? 1 : 0;
            caras.push(`<path d="M ${cx} ${cy} L ${pt(P(p.ini))} A ${rx} ${ry} 0 ${grande} 1 ${pt(P(p.fin))} Z" fill="${p.color}"/>`);
          });
        }
        // Etiquetas de porcentaje con línea guía, separadas para que no se pisen
        const marcas = arcos.map(p => {
          const m = (p.ini + p.fin) / 2;
          const frente = m > Math.PI / 2 && m < 3 * Math.PI / 2;
          const borde = P(m, frente ? h : 0);
          const derecha = Math.sin(m) >= 0;
          return { p, m, borde, derecha, y: borde[1] + (frente ? 2.2 : -2.2) * Math.abs(Math.cos(m)) };
        });
        ['der', 'izq'].forEach(lado => {
          const grupoLado = marcas.filter(mk => (lado === 'der') === mk.derecha).sort((x, y) => x.y - y.y);
          for (let i = 1; i < grupoLado.length; i++) {
            if (grupoLado[i].y - grupoLado[i - 1].y < 2.6) grupoLado[i].y = grupoLado[i - 1].y + 2.6;
          }
        });
        marcas.forEach(mk => {
          const xCodo = mk.borde[0] + (mk.derecha ? 1 : -1) * (Math.abs(Math.sin(mk.m)) < 0.3 ? 0.6 : 2);
          const xTexto = xCodo + (mk.derecha ? 0.8 : -0.8);
          etiquetas.push(`<polyline points="${pt(mk.borde)} ${xCodo.toFixed(3)} ${mk.y.toFixed(3)} ${xTexto.toFixed(3)} ${mk.y.toFixed(3)}" fill="none" stroke="#000" stroke-width="0.12"/>`);
          etiquetas.push(`<text x="${(xTexto + (mk.derecha ? 0.3 : -0.3)).toFixed(3)}" y="${(mk.y + 0.7).toFixed(3)}" text-anchor="${mk.derecha ? 'start' : 'end'}">${porcentaje(mk.p.value)}</text>`);
        });
        return `<svg class="torta" viewBox="0 0 62 47" xmlns="http://www.w3.org/2000/svg">${lados.join('')}${caras.join('')}${etiquetas.join('')}</svg>`;
      })();

      return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Recibo de Haberes</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    @page { size: A4 portrait; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #e6e6e6; }
    body { font-family: 'Courier New', Courier, monospace; color: #000; }
    .page { position: relative; width: 210mm; height: 297mm; margin: 6mm auto; background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,.15); overflow: hidden; }
    .recibo { position: absolute; left: 17.5mm; top: 11.7mm; width: 189mm; border: 0.35mm solid #000; }
    .enc { height: 17.5mm; padding: 0.8mm 0 0 39.2mm; border-bottom: 0.25mm solid #000; line-height: 4.6mm; font-size: 9.5pt; }
    .enc .razon { font-weight: bold; }
    table.b { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.b td { border-left: 0.25mm solid #000; border-bottom: 0.25mm solid #000; height: 5.5mm; padding: 0 1.2mm; font-size: 8pt; font-weight: bold; text-align: center; white-space: nowrap; overflow: hidden; }
    table.b td:first-child { border-left: none; }
    table.b tr.lbl td { background: #cdcdcd; height: 4.3mm; font-size: 7pt; }
    table.b td.izq { text-align: left; }
    .conc { min-height: 34.1mm; padding-bottom: 5mm; border-bottom: 0.25mm solid #000; }
    table.c { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.c th { background: #cdcdcd; height: 4.2mm; font-size: 7pt; font-weight: bold; border-left: 0.25mm solid #000; border-bottom: 0.25mm solid #000; }
    table.c th:first-child { border-left: none; }
    table.c td { height: 4.4mm; padding: 0 1.4mm; font-size: 8pt; white-space: nowrap; overflow: hidden; }
    table.c tr:first-child td { padding-top: 0.6mm; }
    td.c-num { text-align: right; padding-right: 2mm !important; }
    .gris { background: #cdcdcd; }
    .comp { display: grid; grid-template-columns: 61mm 47mm 29.5mm 13mm 19.5mm 17.5mm 1.5mm; height: 4.4mm; align-items: center; font-size: 7.5pt; font-weight: bold; white-space: nowrap; }
    .comp .r { text-align: right; }
    .comp .l { padding-left: 1.2mm; }
    .comp .l2 { padding-left: 1.5mm; }
    .neto { display: grid; grid-template-columns: 60mm 60mm 67.5mm 1.5mm; height: 4.7mm; align-items: center; font-weight: bold; border-bottom: 0.25mm solid #000; white-space: nowrap; }
    .neto .lab { text-align: center; font-size: 9pt; }
    .neto .val { text-align: right; font-size: 10pt; }
    .sonpesos { height: 4.7mm; line-height: 4.7mm; padding-left: 1.2mm; font-size: 6.5pt; border-bottom: 0.25mm solid #000; white-space: nowrap; overflow: hidden; }
    .det { position: relative; height: 65.6mm; }
    .det-titulo { position: absolute; top: 1.4mm; left: 0; width: 127mm; text-align: center; font-size: 8pt; font-weight: bold; }
    .det-titulo span { border-bottom: 0.3mm solid #000; padding-bottom: 0.2mm; }
    .det-grid { position: absolute; top: 7.6mm; left: 1.2mm; width: 123.6mm; display: grid; grid-template-columns: 61.8mm 4.7mm 57.1mm; grid-auto-rows: 13.8mm; }
    .g-fila { display: flex; justify-content: space-between; font-size: 8pt; line-height: 3.3mm; white-space: nowrap; }
    .g-tot { font-weight: bold; }
    .ctot-titulo { position: absolute; top: 3.9mm; left: 127.2mm; width: 61.8mm; text-align: center; font-size: 8pt; font-weight: bold; }
    .leyenda { position: absolute; top: 8.3mm; left: 127.9mm; display: grid; grid-template-columns: 23.5mm 25mm 11mm; row-gap: 0.4mm; font-family: Arial, Helvetica, sans-serif; font-size: 6.3pt; }
    .ley-item { display: flex; align-items: center; gap: 0.8mm; white-space: nowrap; }
    .ley-color { display: inline-block; width: 2.4mm; height: 2.4mm; }
    .torta { position: absolute; top: 15mm; left: 127.2mm; width: 62mm; height: 47mm; overflow: visible; font-family: Arial, Helvetica, sans-serif; font-size: 1.9px; }
    .nota { position: absolute; bottom: 0.8mm; left: 1.2mm; font-size: 7pt; white-space: nowrap; }
    .firma { position: absolute; left: 147mm; top: 236.8mm; width: 59.5mm; text-align: center; font-size: 8pt; }
    .firma img { display: block; margin: 0 auto 1mm auto; max-width: 35mm; max-height: 14mm; }
    .firma .linea { border-top: 0.25mm solid #000; padding-top: 0.6mm; }
    .conformidad { position: absolute; left: 17.5mm; top: 224mm; width: 189mm; font-size: 7pt; text-align: center; }
    .print-btn { position: fixed; top: 12px; right: 16px; z-index: 9999; background: #1976d2; color: #fff; border: none; border-radius: 8px; padding: 10px 22px; font-size: 14px; font-family: Arial, Helvetica, sans-serif; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.25); }
    .print-btn:hover { background: #1565c0; }
    @media print {
      html, body { background: #fff; }
      .page { margin: 0; box-shadow: none; }
      .print-btn { display: none !important; }
      .gris, table.b tr.lbl td, table.c th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .leyenda, .torta { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">&#128438; Imprimir</button>
  <div class="page">
    <div class="recibo">
      <div class="enc">
        <div class="razon">${esc(nombreEmpresa)}</div>
        <div>${esc(direccionEmpresa)}</div>
        <div>C.U.I.T. ${esc(cuitEmpresa)}</div>
      </div>
      <table class="b">
        <colgroup><col style="width:26.3mm"><col style="width:65.8mm"><col style="width:33.8mm"><col style="width:33.8mm"><col></colgroup>
        <tr class="lbl"><td>LEGAJO</td><td>APELLIDO Y NOMBRES</td><td>C.U.I.L</td><td>FECHA INGRESO</td><td>ANTIGÜEDAD</td></tr>
        <tr><td>${esc(legajoTexto(r0.Legajo))}</td><td>${txt(r0.Nombre)}</td><td>${esc(cuilConGuiones(r0.CUIL))}</td><td>${formatearFechaBD(r0.FecIngreso)}</td><td>${antiguedad}</td></tr>
      </table>
      <table class="b">
        <colgroup><col style="width:71.2mm"><col style="width:37.3mm"><col></colgroup>
        <tr class="lbl"><td>LIQUIDACION</td><td>FECHA PAGO</td><td>CBU</td></tr>
        <tr><td class="izq">${liquidacion}</td><td>${formatFechaCorta(r0.FechaPago)}</td><td>${txt(r0.CBU)}</td></tr>
      </table>
      <table class="b">
        <colgroup><col style="width:32mm"><col style="width:86.3mm"><col style="width:41.5mm"><col></colgroup>
        <tr class="lbl"><td>SUELDO/JORNAL</td><td>CATEGORIA LABORAL</td><td>LUGAR DE PAGO</td><td>F. PAGO APORTES</td></tr>
        <tr><td>${fmt(r0.VariableReg2)}</td><td>${txt(r0.CategoriaNombre)}</td><td>${txt(bancoAportes)}</td><td>${fPagoAportes}</td></tr>
      </table>
      <div class="conc">
        <table class="c">
          <colgroup><col style="width:80.4mm"><col style="width:21mm"><col style="width:40.8mm"><col></colgroup>
          <tr><th>CONCEPTO</th><th>UNIDAD</th><th>BASE</th><th>MONTO</th></tr>
          ${filasConceptos}
        </table>
      </div>
      <div class="gris">
        <div class="comp">
          <span class="l">COMPOSICION SALARIAL: Remunerativo:</span><span class="r">${fmt(subtotalHabCRet)}</span>
          <span class="l2">No Remunerativo:</span><span class="r">${fmt(subtotalHabSRet)}</span>
          <span class="l2">Descuentos:</span><span class="r">${fmt(subtotalRet)}</span><span></span>
        </div>
        <div class="neto"><span></span><span class="lab">SUELDO NETO $</span><span class="val">${fmt(neto)}</span><span></span></div>
      </div>
      <div class="sonpesos">Son pesos: ${esc(numeroALetras(Math.floor(neto)).toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''))}</div>
      <div class="det">
        <div class="det-titulo"><span>Detalle de la composición salarial</span></div>
        <div class="det-grid">
          ${grupo('Total Costo Sindical', totalSindical, [['Empleador', sindicalEmp], ['Trabajador', sindicalTra]])}<div></div>
          ${grupo('Total Costo INSSJP', totalInssjp, [['Empleador', inssjpEmp], ['Trabajador', inssjpTra]])}
          ${grupo('Total Seguridad Social', totalSegSocial, [['Empleador', segSocialEmp], ['Trabajador', segSocialTra]])}<div></div>
          ${grupo('Total Costo ART', artEmp, [['Empleador', artEmp]])}
          ${grupo('Total Obra Social', totalObraSocial, [['Empleador', obraSocialEmp], ['Trabajador', obraSocialTra]])}<div></div>
          ${grupo('Total Costo SCVO', scvoEmp, [['Empleador', scvoEmp]])}
          <div></div><div></div>
          ${grupo('Total Otros', totalOtros, [['Empleador', otrosEmp], ['Trabajador', otrosTra]])}
        </div>
        <div class="ctot-titulo">Costo Total Empleador</div>
        <div class="leyenda">${leyenda}</div>
        ${tortaSvg}
        <div class="nota">Nota: Seguridad social del empleador incluye SIPA, Fondo Nacional de Empleo y Asignaciones Familiares</div>
      </div>
    </div>
    <!--FIRMA_INICIO-->
    <div class="firma">
      ${firmaBase64 ? `<img src="${firmaBase64}" alt="Firma Empleador">` : ''}
      <div class="linea">Firma del Empleador</div>
    </div>
    <!--FIRMA_FIN-->
  </div>
</body>
</html>`;
    }

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Recibo de Haberes (Doble Talón)</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    :root{--ink:#000;--muted:#555;--grid:#dcdcdc;--ultra-tiny:6px;--extra-tiny:7.5px;--tiny:9.5px;--sm:11px;--md:12px;--lg:14px;--xl:18px;}
  html,body{margin:0;font-family:Arial,Helvetica,sans-serif;color:var(--ink);font-size:13px}
  .page{width:180mm;min-height:270mm;margin:8mm auto 0mm auto;box-shadow:0 4px 16px rgba(0,0,0,.12);padding:8mm 8mm 8mm 8mm;box-sizing:border-box;position:relative}
  /* .page:before eliminado para quitar la línea divisoria */
    .stub{padding:4mm 4mm 3mm;display:grid;grid-auto-rows:auto;row-gap:0}
    .head{display:flex;align-items:center;gap:15px;border:1px solid var(--ink);border-radius:15px;padding:8px 16px}
    .logo img{height:45px;width:45px;border-radius:8px;object-fit:cover}
    .company{line-height:1.3}
    .company .name{font-weight:bold;font-size:var(--lg)}
    .company .addr,.company .cuit{font-size:var(--md)}
    .row{display:grid;gap:6px}
    .row-1{grid-template-columns:95px 1fr 175px 170px}
    .row-2{grid-template-columns:120px 130px 130px 1fr 1fr}
    .field{display:grid;grid-template-rows:auto 1fr}
    .label{font-size:var(--tiny);text-transform:uppercase;color:#333;letter-spacing:.03em}
    .value{border:2px solid var(--ink);border-radius:8px;padding:5px 8px;font-size:var(--sm)}
    .value.bold{font-weight:bold}
    .value.center{text-align:center}
    .value.right{text-align:right;font-variant-numeric:tabular-nums}
    .band{display:grid;grid-template-columns:1fr 1fr}
    .band .cell{border:1px solid var(--ink);border-bottom:none;text-align:center;padding:4px 0;font-size:var(--tiny);text-transform:uppercase}
    .band-values{display:grid;grid-template-columns:1fr 1fr}
    .band-values .cell{border:1px solid var(--ink);border-top:none;padding:6px 8px;font-size:var(--sm);display:flex;justify-content:center;align-items:center}
    .band-values .cell.split{justify-content:space-between}
    .bank{opacity:.9}
    .conceptos{border:1px solid var(--ink);margin:0 !important;border-radius:0;position:relative}
    .conceptos + .conceptos{border-top:none;margin-bottom:10px}
    table{width:100%;border-collapse:collapse;border-spacing:0;font-size:var(--tiny);margin:0 !important}
    thead th{border-bottom:1px solid var(--ink);border-right:1px solid var(--ink);padding:4px 6px;text-transform:uppercase;font-size:var(--ultra-tiny);margin:0}
    thead th:last-child{border-right:none}
    tbody td{border-right:1px solid var(--ink);border-bottom:none;padding:4px 6px;margin:0;white-space:nowrap;font-size:var(--tiny)}
    tbody td:last-child{border-right:none}
    tbody tr:last-child td{border-bottom:none}
    td.num{text-align:right;font-variant-numeric:tabular-nums}
    td.center{text-align:center}
    td.bold{font-weight:bold}
    col.desc{width:28%}
    col.cant{width:8%}
    col.valor{width:8%}
    col.habcr{width:19%}
    col.habsr{width:18%}
    col.ret{width:19%}
    .tot-right{display:flex;gap:8px;justify-content:flex-end;margin-top:6px}
    .tot-box{border:1px solid var(--ink);min-width:160px}
    .tot-box .t-lab{border-bottom:1px solid var(--ink);padding:4px 8px;font-size:var(--tiny);text-transform:uppercase;text-align:center}
    .tot-box .t-val{padding:6px 8px;font-size:var(--sm);text-align:right;font-variant-numeric:tabular-nums}
    .totgrid{display:grid;grid-template-columns:90px 120px 1fr 150px 120px 150px;gap:0;border:1px solid var(--ink);overflow:hidden}
    .totgrid .cell{border-right:1px solid var(--ink);padding:6px 8px;font-size:var(--sm)}
    .totgrid .cell:last-child{border-right:none}
    .cell .lab{display:block;font-size:var(--tiny);text-transform:uppercase}
    .cell.center{text-align:center}
    .cell.right{text-align:right;font-variant-numeric:tabular-nums}
    .lfp{display:grid;grid-template-columns:1fr;border:1px solid var(--ink);overflow:hidden}
    .lfp .lab{border-bottom:1px solid var(--ink);text-transform:uppercase;font-size:var(--tiny);padding:4px 6px}
    .lfp .val{padding:6px 8px;font-size:var(--sm)}
    .payrow{display:grid;grid-template-columns:1fr;gap:0}
    .sonpesos{border:1px solid var(--ink);border-radius:12px;padding:10px;display:grid;grid-template-columns:1fr auto;gap:10px;align-items:center}
    .sonpesos-left{display:flex;flex-direction:column}
    .sonpesos .lab{font-size:var(--tiny);text-transform:uppercase;font-weight:bold}
    .sonpesos .val{font-size:var(--lg)}
    .neto{border:1px solid var(--ink);border-radius:8px;padding:0;display:flex;flex-direction:column;align-items:center;min-width:160px}
    .neto .lab{font-size:var(--tiny);text-transform:uppercase;text-align:center;border-bottom:1px solid var(--ink);padding:6px 10px 4px 10px;margin:0;width:100%;box-sizing:border-box}
    .neto .val{font-size:var(--lg);font-weight:bold;text-align:center;padding:4px 10px 6px 10px}
    .leyenda{border:1px solid var(--ink);border-radius:8px;padding:8px;font-size:var(--tiny)}
    .firmas{display:grid;grid-template-columns:1fr 1fr;gap:10mm}
    .sign{margin-top:14px;text-align:center;font-size:var(--tiny)}
    .sign:before{content:"";display:block;border-top:1px solid var(--ink);margin:18px 0 6px}
    .sign.no-line:before{display:none}
    .sign img{margin-top:0px}
    .sign:has(img):before{display:none}
    @media print{
      body{background:#fff; margin:0; padding:0;font-size:13px}
      .page{
        box-shadow:none;
        width:180mm;
        min-height:285mm;
        margin:2mm auto 0mm auto;
        padding:4mm 8mm 4mm 8mm;
        max-height:none;
        gap:0 !important;
        grid-gap:0 !important;
      }
      .stub{row-gap:0 !important; grid-row-gap:0 !important; padding:3mm 3mm 2mm; margin:0 !important}
      .head{padding:4px 8px; gap:8px; margin:0 !important}
      .conceptos{margin:0 !important; margin-bottom:0 !important}
      .conceptos + .conceptos{margin:0 !important; margin-bottom:0 !important; margin-top:0 !important}
      table{font-size:var(--ultra-tiny); margin:0 !important}
      tbody td{padding:2px 3px}
      thead th{padding:2px 3px}
      .payrow{margin-top:4px; margin-bottom:0 !important}
      .sonpesos{padding:6px}
      .leyenda{padding:4px}
      .sign{margin-top:8px}
      .sign:before{margin:8px 0 3px}
      .print-btn{display:none !important}
  @page{size:A4 portrait; margin:2mm 2mm 0mm 2mm}
    }
    .print-btn{position:fixed;top:12px;right:16px;z-index:9999;background:#1976d2;color:#fff;border:none;border-radius:8px;padding:10px 22px;font-size:14px;font-family:Arial,Helvetica,sans-serif;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;gap:8px;}
    .print-btn:hover{background:#1565c0;}
  </style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">&#128438; Imprimir</button>
  <div class="page">
    <section class="stub">
      <div class="head">
        <div class="logo">
          ${logoBase64 ? 
            `<img src="${logoBase64}" alt="Logo Empresa" style="width:100px;height:45px;border-radius:8px;object-fit:cover;">` 
            : '<div style="width:100px;height:45px;border-radius:8px;"></div>'
          }
        </div>
        <div class="company">
          <div class="name">${nombreEmpresa}</div>
          <div class="addr">${direccionEmpresa}</div>
          <div class="cuit">${cuitEmpresa}</div>
        </div>
      </div>
      <div class="conceptos" style="border-top-left-radius: 8px; border-top-right-radius: 8px; overflow: hidden;">
        <table style="border-top-left-radius: 8px; border-top-right-radius: 8px; overflow: hidden;">
          <tbody>
            <tr style="border-top: 1px solid var(--ink);">
              <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); font-weight: bold;">Legajo</td>
              <td colspan= "2"style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); font-weight: bold;">Apellido y Nombres</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); font-weight: bold;">N° C.U.I.L.</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); font-weight: bold;">Sueldo o Jornal</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
              <td class="center bold" style="border-right: 1px solid var(--ink);">${registros[0]?.Legajo || ''}</td>
              <td colspan="2" class="bold" style="border-right: 1px solid var(--ink); font-size: var(--tiny);">${registros[0]?.Nombre || ''}</td>
              <td class="center" style="border-right: 1px solid var(--ink);">${registros[0]?.CUIL || ''}</td>
              <td class="num">${sueldoJornal.toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
              <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); font-weight: bold;">Fecha Ingreso</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); font-weight: bold;">Fecha B.Antigüedad</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); font-weight: bold;">Fecha Egreso</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); font-weight: bold;">Centro Costos</td>
              <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); font-weight: bold;">Tarea Desempeñada</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
              <td class="center" style="border-right: 1px solid var(--ink);">${formatearFechaBD(registros[0]?.FecIngreso)}</td>
              <td class="center" style="border-right: 1px solid var(--ink);">${formatearFechaBD(registros[0]?.FecBaseAnt)}</td>
              <td class="center" style="border-right: 1px solid var(--ink);">${registros[0]?.FecEgreso || '-'}</td>
              <td class="center" style="border-right: 1px solid var(--ink);">${registros[0]?.CentroADesc || registros[0]?.CentroA || ''}</td>
              <td class="center">${registros[0]?.AtributoEsp1 || registros[0]?.ConcObs || 'ADMINISTRATIVO'}</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
              <td colspan="2" style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); font-weight: bold;">Periodo Liquidado</td>
              <td colspan="3" style="text-align: center; text-transform: uppercase; font-size: var(--tiny); font-weight: bold;">Depositado en Cuenta Nro</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
              <td colspan="2" class="center" style="border-right: 1px solid var(--ink);">${periodo}</td>
              <td colspan="3" class="center" style="font-size: var(--sm);">${registros[0]?.NroCtaBancaria || ''} ${registros[0]?.DescBco || 'BANCO HSBC'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="conceptos" style="border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; overflow: hidden;">
        <table style="border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; overflow: hidden;">
          <colgroup>
            <col class="desc"><col class="cant"><col class="valor"><col class="habcr"><col class="habsr"><col class="ret">
          </colgroup>
          <thead><tr><th>Descripción</th><th>Cant o %</th><th>Valor Uni</th><th>Haberes C/Ret.</th><th>Haberes S/Ret.</th><th>Retenciones</th></tr></thead>
          <tbody>
            ${filasConceptos.join('\n')}
            <tr >
                <td colspan="3" style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); border-top: 1px solid var(--ink);">Depósito de Aporte Jubilatorio</td>
                <td class="num right" style="border-right: 1px solid var(--ink);"></td>
                <td class="num right" style="border-right: 1px solid var(--ink);"></td>
                <td class="num right"></td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
                <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); width: 10%;">PERIODO</td>
                <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); width: 12%;">FECHA</td>
                <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); border-right: 1px solid var(--ink); width: 12%;">BANCO</td>
                <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); width: 22%;">TOT.HAB.C/RET.</td>
                <td style="text-align: center; text-transform: uppercase; font-size: var(--extra-tiny); border-right: 1px solid var(--ink); width: 22%;">TOT.HAB.S/RET.</td>
                <td style="text-align: center; text-transform: uppercase; font-size: var(--tiny); width: 22%;">RETENCIONES</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
                <td class="center" style="border-right: 1px solid var(--ink); width: 10%;">${periodo}</td>
                <td class="center" style="border-right: 1px solid var(--ink); width: 12%;">${formatFecha(registros[0]?.FechaPago)}</td>
                <td class="center" style="border-right: 1px solid var(--ink); width: 12%;">BANCO GALICIA</td>
                <td class="num right" style="border-right: 1px solid var(--ink); width: 22%;">${formatNumber(subtotalHabCRet)}</td>
                <td class="num right" style="border-right: 1px solid var(--ink); width: 22%;">${formatNumber(subtotalHabSRet)}</td>
                <td class="num right" style="width: 22%; font-size: var(--tiny);">${formatNumber(subtotalRet)}</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
                <td colspan="6" style="text-align: left; text-transform: uppercase; font-size: var(--ultra-tiny); padding: 4px;">Lugar y fecha de pago</td>
            </tr>
            <tr style="border-top: 1px solid var(--ink);">
                <td colspan="6" style="text-align: left; font-size: var(--tiny); padding: 6px;">${registros[0]?.LugarPago || 'CABA'} - ${formatFecha(registros[0]?.FechaPago)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="payrow" style="margin-top: 6px;">
        <div class="sonpesos">
          <div class="sonpesos-left">
            <div class="lab">Son pesos</div>
            <div class="val">${numeroALetras(Math.floor(neto)).toUpperCase()}</div>
          </div>
          <div class="neto">
            <div class="lab">Neto a cobrar</div>
            <div class="val">${formatNumber(neto)}</div>
          </div>
        </div>
      </div>
      ${(() => {
        const contrib = registros.filter(r =>
          (r.ConcClaseLetras === null || r.ConcClaseLetras === '' || r.ConcClaseLetras === undefined) &&
          r.ConcImpCont && parseFloat(r.ConcImpCont) > 0
        );
        if (contrib.length === 0) return '';
        const total = contrib.reduce((sum, r) => sum + parseFloat(r.ConcImpCont || 0), 0);
        const detalle = contrib.map(r => {
          const cant = parseFloat(r.ConcCant || 0);
          const cantStr = cant > 0 ? ` ${cant.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%` : '';
          return `${r.ConcDescr}${cantStr} $ ${parseFloat(r.ConcImpCont).toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        }).join(' - ');
        return `<div style="border:1px solid var(--ink);border-radius:8px;padding:8px;margin-top:6px;font-size:var(--tiny);">
          <div style="font-weight:bold;text-transform:uppercase;margin-bottom:4px;font-size:var(--tiny);">DETALLE ANALITICO DE CONTRIBUCIONES (ART 140 J)</div>
          <div style="margin-bottom:4px;">De acuerdo a la normativa vigente, se detalla la totalidad de los conceptos abonados por el empleador por el período liquidado, los cuales ascienden a la suma de $${total.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} según el siguiente desglose individual:</div>
          <div>${detalle}</div>
        </div>`;
      })()}
      <div class="leyenda">LA PRESENTE LIQUIDACIÓN ES COPIA DEL RECIBO FIRMADO QUE OBRA EN PODER DE LA EMPRESA COMO COMPROBANTE DE PAGO</div>
      <div class="firmas">
        <div></div>
        <div class="sign">
          ${firmaBase64 ? 
            `<img src="${firmaBase64}" alt="Firma Empleador" style="width:120px;height:40px;margin:0px 0 5px 0;display:block;margin-left:auto;margin-right:auto;">` 
            : '<div style="border-top:1px solid var(--ink);margin:18px 0 6px 0;"></div>'
          }
          <div style="border-top:1px solid var(--ink);margin:0px 0;"></div>
          FIRMA DEL EMPLEADOR
        </div>
      </div>
    </section>
  </div>
</body>
</html>`;
  };

  // Reset import form
  const resetImportForm = () => {
    setFile(null);
    setPeriodo('');
    setAnio('');
    setFechaPago('');
    setTipoLiquidacion('mensual');
    setEmpresaImportacion('');
    setProgress(null);
    setPolling(false);
    setCurrentImportId(null);
    setImportError(null);
    setImportErrors([]);
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Modal handlers
  const handleOpenModal = () => {
    resetImportForm();
    setOpenModal(true);
  };
  
  const handleCloseModal = () => {
    setOpenModal(false);
    // Don't reset form automatically when closing to preserve state during import
  };

  const handleForceCloseModal = () => {
    setOpenModal(false);
    resetImportForm();
  };
  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handlePeriodoChange = (e) => setPeriodo(e.target.value);
  const handleAnioChange = (e) => setAnio(e.target.value);
  const handleFechaPagoChange = (e) => setFechaPago(e.target.value);
  const handleTipoLiquidacionChange = (e) => setTipoLiquidacion(e.target.value);

  // Importar recibos
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !periodo || !anio || !fechaPago || !empresaImportacion) {
      setImportError({
        error: 'Faltan campos obligatorios',
        detalles: {
          archivo: !file ? 'Debe seleccionar un archivo' : null,
          empresa: !empresaImportacion ? 'Debe seleccionar la empresa' : null,
          periodo: !periodo ? 'Debe seleccionar el mes de liquidación' : null,
          anio: !anio ? 'Debe especificar el año' : null,
          fechaPago: !fechaPago ? 'Debe especificar la fecha de pago' : null
        }
      });
      return;
    }

    // Clear previous errors
    setImportError(null);
    setImportErrors([]);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('periodoLiquidacion', `${periodo}/${anio}`);
    formData.append('fechaPago', fechaPago);
    formData.append('tipoLiquidacion', tipoLiquidacion);
    formData.append('empresa_id', empresaImportacion);
    setProgress({ processed: 0, total: 0, estimatedTime: null });
    setPolling(true);
    
    try {
  const response = await fetch(`${API_BASE_URL}/api/recibos/importar`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${getToken && getToken()}` }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        // Handle error response
        setImportError(result);
        setPolling(false);
        return;
      }
      
      if (result.importId) {
        setCurrentImportId(result.importId);
      }
    } catch (err) {
      console.error('Error al importar recibos:', err);
      setImportError({
        error: 'Error de conexión',
        detalles: {
          mensaje: 'No se pudo conectar con el servidor',
          sugerencia: 'Verifique su conexión a internet e inténtelo nuevamente'
        }
      });
      setPolling(false);
    }
  };

  // Cancelar importación
  const handleCancel = async () => {
    if (!currentImportId) {
      alert('No hay importación en curso para cancelar.');
      return;
    }
    
    const confirmCancel = window.confirm(
      `¿Estás seguro de que quieres cancelar la importación?\n\nID de importación: ${currentImportId}\n\nEsto eliminará todos los registros de esta importación específica.`
    );
    
    if (!confirmCancel) return;
    
    try {
  await fetch(`${API_BASE_URL}/api/recibos/cancelar`, { 
        method: 'POST', 
        headers: { Authorization: `Bearer ${getToken && getToken()}` } 
      });
      setPolling(false);
      setProgress(null);
      setCurrentImportId(null);
      alert(`Importación ${currentImportId} cancelada y registros eliminados.`);
    } catch (err) {
      alert('Error al cancelar la importación.');
    }
  };

  // Polling de progreso
  useEffect(() => {
    let interval;
    if (polling) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/recibos/progreso`, {
            credentials: 'include',
            headers: { Authorization: `Bearer ${getToken && getToken()}` }
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          setProgress(data);
          // Si una consulta anterior falló pero esta anduvo, sacar ese aviso
          setImportError(prev => (prev?.error === 'Error al obtener progreso de importación' ? null : prev));

          // Actualizar errores si los hay
          if (data.errors && data.errors.length > 0) {
            setImportErrors(data.errors);
          }
          
          // Actualizar currentImportId si viene del servidor
          if (data.currentImportId && data.currentImportId !== currentImportId) {
            setCurrentImportId(data.currentImportId);
          }
          
          if (data.finished) {
            setPolling(false);
            // No resetear currentImportId aquí para mantener el estado final
            clearInterval(interval);
            
            // Refresh recibos list after successful import
            if (!data.hasErrors || data.errorCount < data.total) {
              const fetchRecibos = async () => {
                try {
                  const res = await fetch(`${API_BASE_URL}/api/recibos/mis-recibos`, {
                    headers: { Authorization: `Bearer ${getToken && getToken()}` }
                  });
                  const data = await res.json();
                  setRecibos(data);
                } catch (err) {
                  console.error('Error al recargar recibos:', err);
                }
              };
              fetchRecibos();
            }
          }
        } catch (err) {
          console.error('Error al obtener progreso:', err);
          setImportError({
            error: 'Error al obtener progreso de importación',
            detalles: {
              mensaje: 'No se pudo obtener el estado de la importación',
              sugerencia: 'La importación puede estar ejecutándose. Revise el historial de importaciones.'
            }
          });
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [polling, currentImportId]);

  // Función para renderizar recibos en formato de accordions por año (para todas las vistas)
  const renderRecibosPorAño = () => {
    // Verificar que agrupadosFiltrados esté definido
    if (!agrupadosFiltrados || !Array.isArray(agrupadosFiltrados) || agrupadosFiltrados.length === 0) {
      return null;
    }
    
    const sortedData = agrupadosFiltrados
      .sort(([a], [b]) => {
        const [periodoA] = a.split('_');
        const [periodoB] = b.split('_');
        const [mesA, anioA] = periodoA.split('/').map(Number);
        const [mesB, anioB] = periodoB.split('/').map(Number);
        
        if (anioB !== anioA) return anioB - anioA;
        if (mesB !== mesA) return mesB - mesA;
        
        const [, tipoA] = a.split('_');
        const [, tipoB] = b.split('_');
        if (tipoA === 'sac' && tipoB === 'mensual') return -1;
        if (tipoA === 'mensual' && tipoB === 'sac') return 1;
        return 0;
      });

    
    // Agrupar recibos por año
    const recibosPorAnio = sortedData.reduce((grupos, [claveGrupo, registros]) => {
      const [periodo] = claveGrupo.split('_');
      const [, anio] = periodo.split('/').map(Number);
      
      if (!grupos[anio]) {
        grupos[anio] = [];
      }
      grupos[anio].push([claveGrupo, registros]);
      return grupos;
    }, {});

    // Ordenar años de más reciente a más antiguo
    const aniosOrdenados = Object.keys(recibosPorAnio)
      .map(Number)
      .sort((a, b) => b - a);


    return aniosOrdenados.map((anio, anioIndex) => {
      const recibosDelAnio = recibosPorAnio[anio];
      
      // Validar que recibosDelAnio existe y es un array
      if (!recibosDelAnio || !Array.isArray(recibosDelAnio) || recibosDelAnio.length === 0) {
        return null;
      }
      
      const totalRecibos = recibosDelAnio.length;
      const recibosFirmados = recibosDelAnio.filter(([claveGrupo]) => firma[claveGrupo]).length;

      return (
        <Accordion 
          key={anio}
          defaultExpanded={anioIndex === 0} // Expandir el año más reciente por defecto
          sx={{ 
            mb: 2,
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&.Mui-expanded': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&.Mui-expanded': {
                borderRadius: '8px 8px 0 0',
              },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📅 Año {anio}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${totalRecibos} recibos`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
                <Chip 
                  label={`${recibosFirmados}/${totalRecibos} firmados`}
                  size="small"
                  color={recibosFirmados === totalRecibos ? 'success' : 'warning'}
                  sx={{ 
                    backgroundColor: recibosFirmados === totalRecibos ? 'success.light' : 'warning.light',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
            {isMobile ? (
              // Renderizado móvil: Cards apiladas
              recibosDelAnio.map(([claveGrupo, registros], index) => {
                const { periodo, tipoLiquidacion } = parseClaveGrupo(claveGrupo);
                const tipoTexto = tipoLiquidacion === 'sac' ? 'SAC (Aguinaldo)' : 'Mensual';
                const puedeVer = puedeVerRecibo(claveGrupo);
                const estaFirmado = firma[claveGrupo];

                return (
                  <Card 
                    key={claveGrupo} 
                    sx={{ 
                      mb: 2, 
                      mx: 1,
                      borderRadius: 2,
                      border: puedeVer ? '1px solid #e2e8f0' : '1px solid #f44336',
                      opacity: puedeVer ? 1 : 0.7,
                      backgroundColor: puedeVer ? 'white' : '#fafafa',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      {/* Header del card */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {periodo}
                          </Typography>
                          <Chip 
                            label={tipoTexto} 
                            color={tipoLiquidacion === 'sac' ? 'secondary' : 'primary'} 
                            size="small" 
                            variant="filled"
                            sx={{ 
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                            }}
                          />
                          {!puedeVer && (
                            <Chip 
                              label="🔒 Bloqueado" 
                              color="error" 
                              size="small" 
                              variant="filled"
                              sx={{ 
                                ml: 1,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                        </Box>
                        <Chip 
                          label={estaFirmado ? '✅ Firmado' : '⏳ Pendiente'}
                          color={estaFirmado ? 'success' : 'warning'}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>

                      {/* Información del empleado */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          👤 {registros[0]?.Nombre || 'N/A'}
                        </Typography>
                        {(() => {
                          const empresasUnicas = [...new Set(registros.map(r => r.empresa_nombre).filter(Boolean))];
                          const legajosUnicos = [...new Set(registros.map(r => r.Legajo).filter(Boolean))];
                          
                          // Si es superadmin, mostrar información adicional del empleado
                          const isSuperAdmin = user && user.rol === 'superadmin';
                          const empleadoNombre = registros[0]?.empleado_nombre;
                          const empleadoApellido = registros[0]?.empleado_apellido;
                          const empleadoEmail = registros[0]?.empleado_email;
                          
                          if (empresasUnicas.length > 1) {
                            return (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {empresasUnicas.length} empresas
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Leg. {legajosUnicos.join(', ')}
                                </Typography>
                                {isSuperAdmin && empleadoEmail && (
                                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                    📧 {empleadoEmail}
                                  </Typography>
                                )}
                              </Box>
                            );
                          } else {
                            return (
                              <Box>
                                {empresasUnicas[0] && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {empresasUnicas[0]}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  Leg. {registros[0]?.Legajo || 'N/A'}
                                </Typography>
                                {isSuperAdmin && (empleadoNombre || empleadoApellido) && (
                                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                    👤 {empleadoNombre} {empleadoApellido}
                                  </Typography>
                                )}
                                {isSuperAdmin && empleadoEmail && (
                                  <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                    📧 {empleadoEmail}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                        })()}
                      </Box>
                    </CardContent>

                    {/* Acciones */}
                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleVerDetalle(claveGrupo)}
                          disabled={!puedeVer}
                          sx={{ 
                            minWidth: 0,
                            flex: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Ver
                        </Button>

                        {!estaFirmado && puedeVer && tienePermiso('recibos', 'firmar') && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CreateIcon />}
                            onClick={() => handleFirmar(claveGrupo)}
                            sx={{ 
                              minWidth: 0,
                              flex: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            Firmar
                          </Button>
                        )}

                        {estaFirmado && (
                          <>
                            <Tooltip title="Ver PDF" arrow>
                              <IconButton
                                color="secondary"
                                size="medium"
                                onClick={() => handleVerPdf(claveGrupo)}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: 'secondary.50'
                                  }
                                }}
                              >
                                <PdfIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="PDF Empleado" arrow>
                              <IconButton
                                color="secondary"
                                size="medium"
                                onClick={() => handleVerPdfEmpleado(claveGrupo)}
                                sx={{ 
                                  border: '1px dashed',
                                  borderColor: 'secondary.main',
                                  '&:hover': {
                                    backgroundColor: 'secondary.50'
                                  }
                                }}
                              >
                                <PdfIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Imprimir Recibo" arrow>
                              <IconButton
                                color="info"
                                size="medium"
                                onClick={() => handleImprimirRecibo(claveGrupo)}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'info.main',
                                  '&:hover': {
                                    backgroundColor: 'info.50'
                                  }
                                }}
                              >
                                <PrintIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Imprimir Recibo Empleado" arrow>
                              <IconButton
                                color="warning"
                                size="medium"
                                onClick={() => handleImprimirReciboEmpleado(claveGrupo)}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'warning.main',
                                  '&:hover': {
                                    backgroundColor: 'warning.50'
                                  }
                                }}
                              >
                                <PrintIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </CardActions>
                  </Card>
                );
              })
            ) : (
              // Renderizado desktop: Tabla dentro del accordion
              <TableContainer 
                component={Paper} 
                elevation={0} 
                sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: '#e2e8f0',
                  backgroundColor: 'white',
                  mt: 1
                }}
              >
                <Table sx={{ minWidth: isTablet ? 500 : 650 }} aria-label={`tabla de recibos del año ${anio}`}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: '#f8fafc',
                      '& th': {
                        borderBottom: '2px solid #e2e8f0'
                      }
                    }}>
                      <TableCell sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem',
                        py: 2
                      }}>
                        📅 Período de Liquidación
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem',
                        py: 2
                      }}>
                        👤 Empleado
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem',
                        py: 2
                      }}>
                        🏢 Empresa
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem',
                        py: 2
                      }}>
                        📋 Estado
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold', 
                        fontSize: '0.9rem',
                        py: 2,
                        width: '200px'
                      }}>
                        ⚡ Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recibosDelAnio.map(([claveGrupo, registros], index) => {
                      const { periodo, tipoLiquidacion } = parseClaveGrupo(claveGrupo);
                      const tipoTexto = tipoLiquidacion === 'sac' ? 'SAC (Aguinaldo)' : 'Mensual';
                      const puedeVer = puedeVerRecibo(claveGrupo);
                      const estaFirmado = firma[claveGrupo];

                      return (
                        <TableRow 
                          key={claveGrupo}
                          sx={{ 
                            '&:nth-of-type(odd)': { 
                              backgroundColor: '#fafbfc' 
                            },
                            '&:hover': { 
                              backgroundColor: '#f0f9ff',
                              transform: 'scale(1.005)',
                              transition: 'all 0.2s ease-in-out'
                            },
                            opacity: puedeVer ? 1 : 0.6,
                            border: puedeVer ? 'none' : '2px solid #ffebee'
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {periodo}
                              </Typography>
                              <Chip 
                                label={tipoTexto} 
                                color={tipoLiquidacion === 'sac' ? 'secondary' : 'primary'} 
                                size="small" 
                                variant="filled"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}
                              />
                              {!puedeVer && (
                                <Chip 
                                  label="🔒 Bloqueado" 
                                  color="error" 
                                  size="small" 
                                  variant="filled"
                                  sx={{ 
                                    ml: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {registros[0]?.Nombre || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Leg. {registros[0]?.Legajo || 'N/A'}
                            </Typography>
                            {(() => {
                              const isSuperAdmin = user && user.rol === 'superadmin';
                              const empleadoNombre = registros[0]?.empleado_nombre;
                              const empleadoApellido = registros[0]?.empleado_apellido;
                              const empleadoEmail = registros[0]?.empleado_email;
                              
                              if (isSuperAdmin && (empleadoNombre || empleadoApellido)) {
                                return (
                                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                    👤 {empleadoNombre} {empleadoApellido}
                                  </Typography>
                                );
                              }
                              if (isSuperAdmin && empleadoEmail) {
                                return (
                                  <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                    📧 {empleadoEmail}
                                  </Typography>
                                );
                              }
                              return null;
                            })()}
                          </TableCell>
                          
                          <TableCell>
                            {(() => {
                              const empresasUnicas = [...new Set(registros.map(r => r.empresa_nombre).filter(Boolean))];
                              
                              if (empresasUnicas.length > 1) {
                                return (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {empresasUnicas.length} empresas
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {empresasUnicas.slice(0, 2).join(', ')}
                                      {empresasUnicas.length > 2 && '...'}
                                    </Typography>
                                  </Box>
                                );
                              } else {
                                return (
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {empresasUnicas[0] || 'N/A'}
                                  </Typography>
                                );
                              }
                            })()}
                          </TableCell>
                          
                          <TableCell>
                            <Chip 
                              label={estaFirmado ? '✅ Firmado' : '⏳ Pendiente'}
                              color={estaFirmado ? 'success' : 'warning'}
                              variant="filled"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleVerDetalle(claveGrupo)}
                                disabled={!puedeVer}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: puedeVer ? 'primary.main' : 'grey.400',
                                  '&:hover': {
                                    backgroundColor: 'primary.50'
                                  }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              {!estaFirmado && puedeVer && tienePermiso('recibos', 'firmar') && (
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleFirmar(claveGrupo)}
                                  sx={{ 
                                    border: '1px solid',
                                    borderColor: 'success.main',
                                    '&:hover': {
                                      backgroundColor: 'success.50'
                                    }
                                  }}
                                >
                                  <CreateIcon fontSize="small" />
                                </IconButton>
                              )}

                              {estaFirmado && (
                                <>
                                  <Tooltip title="Ver PDF" arrow>
                                    <IconButton
                                      color="secondary"
                                      size="small"
                                      onClick={() => handleVerPdf(claveGrupo)}
                                      sx={{ 
                                        border: '1px solid',
                                        borderColor: 'secondary.main',
                                        '&:hover': {
                                          backgroundColor: 'secondary.50'
                                        }
                                      }}
                                    >
                                      <PdfIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="PDF Empleado" arrow>
                                    <IconButton
                                      color="secondary"
                                      size="small"
                                      onClick={() => handleVerPdfEmpleado(claveGrupo)}
                                      sx={{ 
                                        border: '1px dashed',
                                        borderColor: 'secondary.main',
                                        '&:hover': {
                                          backgroundColor: 'secondary.50'
                                        }
                                      }}
                                    >
                                      <PdfIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Imprimir Recibo" arrow>
                                    <IconButton
                                      color="info"
                                      size="small"
                                      onClick={() => handleImprimirRecibo(claveGrupo)}
                                      sx={{ 
                                        border: '1px solid',
                                        borderColor: 'info.main',
                                        '&:hover': {
                                          backgroundColor: 'info.50'
                                        }
                                      }}
                                    >
                                      <PrintIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Imprimir Recibo Empleado" arrow>
                                    <IconButton
                                      color="warning"
                                      size="small"
                                      onClick={() => handleImprimirReciboEmpleado(claveGrupo)}
                                      sx={{ 
                                        border: '1px solid',
                                        borderColor: 'warning.main',
                                        '&:hover': {
                                          backgroundColor: 'warning.50'
                                        }
                                      }}
                                    >
                                      <PrintIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // Función para renderizar recibos en formato de cards para móvil
  const renderMobileCards = () => {
    // Verificar que agrupadosFiltrados esté definido
    if (!agrupadosFiltrados || !Array.isArray(agrupadosFiltrados) || agrupadosFiltrados.length === 0) {
      return null;
    }
    
    const sortedData = agrupadosFiltrados
      .sort(([a], [b]) => {
        const [periodoA] = a.split('_');
        const [periodoB] = b.split('_');
        const [mesA, anioA] = periodoA.split('/').map(Number);
        const [mesB, anioB] = periodoB.split('/').map(Number);
        
        if (anioB !== anioA) return anioB - anioA;
        if (mesB !== mesA) return mesB - mesA;
        
        const [, tipoA] = a.split('_');
        const [, tipoB] = b.split('_');
        if (tipoA === 'sac' && tipoB === 'mensual') return -1;
        if (tipoA === 'mensual' && tipoB === 'sac') return 1;
        return 0;
      });

    
    // Agrupar recibos por año
    const recibosPorAnio = sortedData.reduce((grupos, [claveGrupo, registros]) => {
      const [periodo] = claveGrupo.split('_');
      const [, anio] = periodo.split('/').map(Number);
      
      if (!grupos[anio]) {
        grupos[anio] = [];
      }
      grupos[anio].push([claveGrupo, registros]);
      return grupos;
    }, {});

    // Ordenar años de más reciente a más antiguo
    const aniosOrdenados = Object.keys(recibosPorAnio)
      .map(Number)
      .sort((a, b) => b - a);


    return aniosOrdenados.map((anio, anioIndex) => {
      const recibosDelAnio = recibosPorAnio[anio];
      
      // Validar que recibosDelAnio existe y es un array
      if (!recibosDelAnio || !Array.isArray(recibosDelAnio) || recibosDelAnio.length === 0) {
        return null;
      }
      
      const totalRecibos = recibosDelAnio.length;
      const recibosFirmados = recibosDelAnio.filter(([claveGrupo]) => firma[claveGrupo]).length;

      return (
        <Accordion 
          key={anio}
          defaultExpanded={anioIndex === 0} // Expandir el año más reciente por defecto
          sx={{ 
            mb: 2,
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&.Mui-expanded': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&.Mui-expanded': {
                borderRadius: '8px 8px 0 0',
              },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📅 Año {anio}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip 
                  label={`${totalRecibos} recibos`}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
                <Chip 
                  label={`${recibosFirmados}/${totalRecibos} firmados`}
                  size="small"
                  color={recibosFirmados === totalRecibos ? 'success' : 'warning'}
                  sx={{ 
                    backgroundColor: recibosFirmados === totalRecibos ? 'success.light' : 'warning.light',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {recibosDelAnio.map(([claveGrupo, registros], index) => {
              const { periodo, tipoLiquidacion } = parseClaveGrupo(claveGrupo);
              const tipoTexto = tipoLiquidacion === 'sac' ? 'SAC (Aguinaldo)' : 'Mensual';
              const puedeVer = puedeVerRecibo(claveGrupo);
              const estaFirmado = firma[claveGrupo];

              return (
                <Card 
                  key={claveGrupo} 
                  sx={{ 
                    mb: 2, 
                    mx: 1,
                    borderRadius: 2,
                    border: puedeVer ? '1px solid #e2e8f0' : '1px solid #f44336',
                    opacity: puedeVer ? 1 : 0.7,
                    backgroundColor: puedeVer ? 'white' : '#fafafa',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)',
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    {/* Header del card */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {periodo}
                        </Typography>
                        <Chip 
                          label={tipoTexto} 
                          color={tipoLiquidacion === 'sac' ? 'secondary' : 'primary'} 
                          size="small" 
                          variant="filled"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        />
                        {!puedeVer && (
                          <Chip 
                            label="🔒 Bloqueado" 
                            color="error" 
                            size="small" 
                            variant="filled"
                            sx={{ 
                              ml: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                            }}
                          />
                        )}
                      </Box>
                      <Chip 
                        label={estaFirmado ? '✅ Firmado' : '⏳ Pendiente'}
                        color={estaFirmado ? 'success' : 'warning'}
                        size="small"
                        variant="filled"
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>

                    {/* Información del empleado */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        👤 {registros[0]?.Nombre || 'N/A'}
                      </Typography>
                      {(() => {
                        const empresasUnicas = [...new Set(registros.map(r => r.empresa_nombre).filter(Boolean))];
                        const legajosUnicos = [...new Set(registros.map(r => r.Legajo).filter(Boolean))];
                        
                        if (empresasUnicas.length > 1) {
                          return (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {empresasUnicas.length} empresas
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Leg. {legajosUnicos.join(', ')}
                              </Typography>
                            </Box>
                          );
                        } else {
                          return (
                            <Box>
                              {empresasUnicas[0] && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {empresasUnicas[0]}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Leg. {registros[0]?.Legajo || 'N/A'}
                              </Typography>
                            </Box>
                          );
                        }
                      })()}
                    </Box>
                  </CardContent>

                  {/* Acciones */}
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleVerDetalle(claveGrupo)}
                        disabled={!puedeVer}
                        sx={{ 
                          minWidth: 0,
                          flex: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        Ver
                      </Button>

                      {!estaFirmado && puedeVer && tienePermiso('recibos', 'firmar') && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CreateIcon />}
                          onClick={() => handleFirmar(claveGrupo)}
                          sx={{ 
                            minWidth: 0,
                            flex: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Firmar
                        </Button>
                      )}

                      {estaFirmado && (
                        <>
                          <Tooltip title="Ver PDF" arrow>
                            <IconButton
                              color="secondary"
                              size="medium"
                              onClick={() => handleVerPdf(claveGrupo)}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'secondary.main',
                                '&:hover': {
                                  backgroundColor: 'secondary.50'
                                }
                              }}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="PDF Empleado" arrow>
                            <IconButton
                              color="secondary"
                              size="medium"
                              onClick={() => handleVerPdfEmpleado(claveGrupo)}
                              sx={{ 
                                border: '1px dashed',
                                borderColor: 'secondary.main',
                                '&:hover': {
                                  backgroundColor: 'secondary.50'
                                }
                              }}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir Recibo" arrow>
                            <IconButton
                              color="info"
                              size="medium"
                              onClick={() => handleImprimirRecibo(claveGrupo)}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'info.main',
                                '&:hover': {
                                  backgroundColor: 'info.50'
                                }
                              }}
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Imprimir Recibo Empleado" arrow>
                            <IconButton
                              color="warning"
                              size="medium"
                              onClick={() => handleImprimirReciboEmpleado(claveGrupo)}
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'warning.main',
                                '&:hover': {
                                  backgroundColor: 'warning.50'
                                }
                              }}
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </CardActions>
                </Card>
              );
            })}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // Función para manejar búsqueda de recibos (superadmin)
  const handleBuscarRecibos = () => {
    if (busqueda.trim().length >= 2) {
      setBusquedaActiva(busqueda.trim());
    }
  };

  // Función para limpiar búsqueda
  const handleLimpiarBusqueda = () => {
    setBusqueda('');
    setBusquedaActiva('');
  };

  return (
    <Box sx={{ 
      p: isMobile ? 1 : 3,
      maxWidth: '100vw',
      minHeight: '100vh',
      overflow: 'visible',
      // Solo prevenir overscroll horizontal, permitir todo lo demás
      overscrollBehaviorX: 'none'
    }}>
      {/* Header Section con título moderno */}
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#1976d2',
          mb: 3
        }}
      >
      
      </Typography>
     
      {/* Buscador para superadmin */}
      {user && user.rol === 'superadmin' && (
        <Box sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 3,
          backgroundColor: 'rgba(33, 150, 243, 0.05)',
          border: '1px solid rgba(33, 150, 243, 0.2)'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              color: '#1976d2',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            🔍 Buscar Recibos
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: 'text.secondary'
            }}
          >
            Ingresa al menos 2 caracteres para buscar por DNI, Legajo, Apellido o Nombre
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}>
            <TextField
              label="DNI, Legajo, Apellido o Nombre"
              variant="outlined"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && busqueda.trim().length >= 2) {
                  handleBuscarRecibos();
                }
              }}
              sx={{ 
                flex: 1,
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
              helperText={busqueda.length > 0 && busqueda.length < 2 ? 'Mínimo 2 caracteres' : ''}
            />
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleBuscarRecibos}
              disabled={busqueda.trim().length < 2}
              sx={{ 
                height: 56,
                px: 4,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Buscar
            </Button>
            {busquedaActiva && (
              <Button 
                variant="outlined" 
                color="secondary"
                onClick={handleLimpiarBusqueda}
                sx={{ 
                  height: 56,
                  px: 4,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Limpiar
              </Button>
            )}
          </Box>
          {busquedaActiva && (
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'success.light',
                color: 'success.dark',
                fontWeight: 600
              }}
            >
              Mostrando resultados para: "{busquedaActiva}" ({recibos.length} recibos encontrados)
            </Typography>
          )}
          {!busquedaActiva && (
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'warning.light',
                color: 'warning.dark',
                fontWeight: 600
              }}
            >
              ⚠️ Ingresa un criterio de búsqueda para ver los recibos
            </Typography>
          )}
        </Box>
      )}

      {isAdmin && (
        <Box sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 3,
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
          border: '1px solid rgba(25, 118, 210, 0.1)'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              color: '#1976d2',
              fontWeight: 600
            }}
          >
            Panel de Administración
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minHeight: 42,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                transition: 'all 0.3s ease'
              }
            }
          }}>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={handleOpenModal}
            >
              Importar Recibos
            </Button>
            <Button 
              variant="contained" 
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory(true)}
            >
              Ver Historial de Importaciones
            </Button>
            <Button 
              variant="contained"
              startIcon={<BusinessIcon />}
              onClick={() => setShowEmpresas(true)}
              sx={{
                backgroundColor: '#ff9800',
                '&:hover': {
                  backgroundColor: '#f57c00',
                }
              }}
            >
              Gestionar Empresas
            </Button>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => { cargarPagosAportes(); setShowPagosAportes(true); }}
              sx={{
                backgroundColor: '#5e35b1',
                '&:hover': {
                  backgroundColor: '#4527a0',
                }
              }}
            >
              Cargar Pago de Aportes
            </Button>
          </Box>
        </Box>
      )}

      {/* Selector de Empresas para Usuarios con Múltiples Legajos */}
      {misLegajos.length > 1 && (
        <Box sx={{ 
          p: 3, 
          mb: 4,
          borderRadius: 3,
          backgroundColor: 'rgba(33, 150, 243, 0.05)',
          border: '1px solid rgba(33, 150, 243, 0.1)'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              color: '#2196f3',
              fontWeight: 600
            }}
          >
            Seleccionar Empresa
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2, 
            alignItems: 'center', 
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row',
            '& .MuiTextField-root': {
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 0.04)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'white',
                  boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.2)',
                }
              }
            }
          }}>
            <TextField
              select
              label="Empresa"
              value={empresaSeleccionada}
              onChange={(e) => setEmpresaSeleccionada(e.target.value)}
              sx={{ 
                minWidth: isMobile ? '100%' : 250,
                width: isMobile ? '100%' : 'auto'
              }}
              size="small"
            >
              <MenuItem value="todas">Todas las empresas</MenuItem>
              {misLegajos.map((legajo) => (
                <MenuItem key={legajo.id} value={legajo.empresa_id}>
                  {legajo.empresa_nombre} (Legajo: {legajo.numero_legajo})
                </MenuItem>
              ))}
            </TextField>
          </Box>
          
          {empresaSeleccionada !== 'todas' && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`Mostrando recibos para: ${misLegajos.find(l => l.empresa_id.toString() === empresaSeleccionada)?.empresa_nombre}`}
                color="info"
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
              />
            </Box>
          )}
        </Box>
      )}

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          bgcolor: 'background.paper', 
          p: isMobile ? 2 : 4, 
          borderRadius: 2, 
          boxShadow: 24, 
          width: isMobile ? '95vw' : 500,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>Importar Recibos</Typography>
          
          {/* Error de importación */}
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>{importError.error}</AlertTitle>
              {importError.detalles && (
                <Box sx={{ mt: 1 }}>
                  {Object.entries(importError.detalles).map(([key, value]) => 
                    value && (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        • {value}
                      </Typography>
                    )
                  )}
                </Box>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={handleFileChange} 
              style={{ marginBottom: 16 }} 
              disabled={polling}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Empresa"
                  value={empresaImportacion}
                  onChange={(e) => setEmpresaImportacion(e.target.value)}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  disabled={polling}
                  helperText="Empresa a la que pertenecen estos recibos"
                >
                  {empresasActivas.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>{emp.nombre}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={8} sm={7}>
                <TextField
                  select
                  label="Mes de Liquidación"
                  value={periodo}
                  onChange={handlePeriodoChange}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  disabled={polling}
                >
                  {months.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4} sm={5}>
                <TextField
                  label="Año"
                  type="number"
                  value={anio}
                  onChange={handleAnioChange}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  inputProps={{ min: 2000, max: 2100 }}
                  disabled={polling}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha de Pago"
                  type="date"
                  value={fechaPago}
                  onChange={handleFechaPagoChange}
                  required
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputLabelProps={{ shrink: true }}
                  disabled={polling}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo de Liquidación"
                  value={tipoLiquidacion} 
                  onChange={handleTipoLiquidacionChange} 
                  required 
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  disabled={polling}
                >
                  <MenuItem value="mensual">Mensual</MenuItem>
                  <MenuItem value="sac">SAC (Aguinaldo)</MenuItem>
                  <MenuItem value="abandono de trabajo">Abandono de Trabajo</MenuItem>
                  <MenuItem value="cesion">Cesión</MenuItem>
                  <MenuItem value="desvinculacion">Desvinculación</MenuItem>
                  <MenuItem value="fin de periodo a prueba">Fin de Periodo a Prueba</MenuItem>
                  <MenuItem value="renuncia">Renuncia</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={2} 
              sx={{ mb: 2 }}
            >
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={polling}
                fullWidth={isMobile}
              >
                {polling ? 'Importando...' : 'Importar'}
              </Button>
              
              {/* Botón de cerrar y limpiar */}
              <Button 
                variant="outlined" 
                color="secondary"
                onClick={handleForceCloseModal}
                disabled={polling}
                fullWidth={isMobile}
              >
                Cerrar y Limpiar
              </Button>
            </Stack>
          </form>

          {/* Progreso de importación */}
          {progress && progress.total > 0 && (
            <Box sx={{ my: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={Math.round((progress.processed / progress.total) * 100)} 
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Procesados: {progress.processed} / {progress.total}
                {progress.estimatedTime !== null && ` | Tiempo estimado restante: ${Math.round(progress.estimatedTime)} seg`}
              </Typography>
              
              {/* Información de errores en progreso */}
              {progress.hasErrors && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                  ⚠️ {progress.errorCount} errores detectados durante la importación
                </Typography>
              )}
              
              {currentImportId && (
                <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                  ID de importación: {currentImportId}
                </Typography>
              )}
              
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleCancel} 
                disabled={progress.finished || !polling}
                sx={{ mb: 2 }}
              >
                Detener importación
              </Button>
              
              {/* Resultado final */}
              {progress.finished && (
                <Box>
                  {progress.hasErrors ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <AlertTitle>Importación completada con errores</AlertTitle>
                      <Typography variant="body2">
                        Se procesaron {progress.processed} registros con {progress.errorCount} errores.
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>¡Importación exitosa!</AlertTitle>
                      <Typography variant="body2">
                        Se importaron {progress.processed} recibos correctamente.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Mostrar errores detallados */}
          {importErrors && importErrors.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography color="error" sx={{ fontWeight: 'bold' }}>
                  Errores de importación ({importErrors.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {importErrors.slice(0, 10).map((error, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={`Fila ${error.fila}: ${error.datos}`}
                        secondary={error.errores.join('; ')}
                        primaryTypographyProps={{ color: 'error', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                  {importErrors.length > 10 && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      ... y {importErrors.length - 10} errores más. Revise el historial para ver todos los detalles.
                    </Typography>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Modal>
      <Box sx={{ mt: 3 }}>
        <Typography variant="h4" sx={{ 
          mb: 3, 
          fontWeight: 'bold', 
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
           Mis Recibos de Sueldo
        </Typography>
        
        {/* Información sobre validación con diseño moderno */}
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: '#f8fafc',
          border: '1px solid', 
          borderColor: '#e2e8f0',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: '#3b82f6',
            borderRadius: '2px 0 0 2px'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              bgcolor: 'info.main', 
              color: 'white', 
              borderRadius: '50%', 
              p: 1,
              minWidth: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem'
            }}>
              📋
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'info.dark', mb: 1 }}>
                Proceso de Validación
              </Typography>
              <Typography variant="body2" color="info.dark" sx={{ lineHeight: 1.6 }}>
                Para acceder a un recibo más reciente, debe firmar todos los recibos anteriores en orden cronológico. 
                Los recibos bloqueados aparecerán marcados hasta que complete este proceso.
              </Typography>
            </Box>
          </Box>
        </Paper>
        
        {/* Contenedor principal de recibos */}
        {agrupadosFiltrados.length === 0 ? (
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center', 
            bgcolor: 'grey.50',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'grey.300',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
          }}>
            <Box sx={{ 
              fontSize: '4rem', 
              mb: 2,
              opacity: 0.7,
              filter: 'grayscale(20%)'
            }}>
              📄
            </Box>
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
              No hay recibos disponibles
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Los recibos aparecerán aquí una vez que sean procesados por el área de RRHH
            </Typography>
            <Box sx={{ 
              bgcolor: 'info.50', 
              p: 2, 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'info.200',
              maxWidth: 500,
              mx: 'auto'
            }}>
              <Typography variant="body2" color="info.dark">
                💡 <strong>Consejo:</strong> Los recibos suelen estar disponibles los primeros días de cada mes
              </Typography>
            </Box>
          </Paper>
        ) : (
          // Vista unificada con accordions por año (tanto móvil como desktop)
          <Box sx={{ 
            width: '100%',
            pb: 2
          }}>
            {renderRecibosPorAño()}
          </Box>
        )}
      </Box>

      {/* Modal de Detalle de Recibo */}
      <Modal open={openDetalle} onClose={handleCloseDetalle}>
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          bgcolor: 'background.paper', 
          p: isMobile ? 2 : 4, 
          borderRadius: 2, 
          boxShadow: 24, 
          width: isMobile ? '95vw' : 700,
          maxHeight: '90vh', 
          overflowY: 'auto' 
        }}>
          {detalle && detalle.registros.length > 0 && (
            <>
              {/* Encabezado empresa y empleado */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{detalle.registros[0]?.empresa_nombre || detalle.registros[0]?.empresa_razon_social || 'Compañía Integral de Alimentos SA'}</Typography>
                  <Typography variant="body2">{detalle.registros[0]?.empresa_direccion || 'Bolivar 187 3º C - Capital Federal'}</Typography>
                  <Typography variant="body2">{detalle.registros[0]?.empresa_cuit || '33-58648427-9'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>LEG. {detalle.registros[0].Legajo}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{detalle.registros[0].Nombre}</Typography>
                  <Typography variant="body2">CUIL: {detalle.registros[0].CUIL}</Typography>
                  <Typography variant="body2">Sueldo: {formatNumber(detalle.registros[0].ConcImpHabCRet || detalle.registros[0].ConcImpHabSRet || detalle.registros[0].ConcImpRet)}</Typography>
                </Box>
              </Box>
              {/* Fechas y centro de costos */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2">Fecha Ingreso: {formatearFechaBD(detalle.registros[0].FecIngreso)}</Typography>
                  <Typography variant="body2">Fecha B.Antiguedad: {formatearFechaBD(detalle.registros[0].FecBaseAnt)}</Typography>
                  <Typography variant="body2">Fecha Egreso: {detalle.registros[0].FecEgreso}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">Centro de Costos: {detalle.registros[0].CentroADesc || detalle.registros[0].CentroA}</Typography>
                  <Typography variant="body2">Tarea: {detalle.registros[0].AtributoEsp1 || detalle.registros[0].ConcObs}</Typography>
                </Box>
              </Box>
              {/* Periodo y banco */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography variant="body2">Período Liquidado: {detalle.periodo}</Typography>
                <Typography variant="body2">Depositado en cuenta: {detalle.registros[0].NroCtaBancaria} {detalle.registros[0].DescBco}</Typography>
              </Box>
              {/* Tabla de conceptos */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Detalle de conceptos</Typography>
              <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: '0.95em' }}>
                <thead>
                  <tr style={{ background: '#eee' }}>
                    <th style={{ textAlign: 'left' }}>Descripción</th>
                    <th style={{ textAlign: 'right' }}>Cant o %</th>
                    <th style={{ textAlign: 'right' }}>Valor Uni</th>
                    <th style={{ textAlign: 'right' }}>Haberes C/Ret</th>
                    <th style={{ textAlign: 'right' }}>Haberes S/Ret</th>
                    <th style={{ textAlign: 'right' }}>Retenciones</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.registros
                    .filter(r => {
                      // Mostrar solo si alguno de los importes es distinto de cero
                      const habCRet = parseFloat(r.ConcImpHabCRet) || 0;
                      const habSRet = parseFloat(r.ConcImpHabSRet) || 0;
                      const ret = parseFloat(r.ConcImpRet) || 0;
                      return habCRet !== 0 || habSRet !== 0 || ret !== 0;
                    })
                    .sort((a, b) => {
                      // Ordenar conceptos: primero haberes c/ret, luego haberes s/ret, luego retenciones
                      const habCRetA = parseFloat(a.ConcImpHabCRet) || 0;
                      const habSRetA = parseFloat(a.ConcImpHabSRet) || 0;
                      const retA = parseFloat(a.ConcImpRet) || 0;
                      
                      const habCRetB = parseFloat(b.ConcImpHabCRet) || 0;
                      const habSRetB = parseFloat(b.ConcImpHabSRet) || 0;
                      const retB = parseFloat(b.ConcImpRet) || 0;
                      
                      // Determinar el tipo de concepto para A
                      let tipoA = 3; // Por defecto, otros
                      if (habCRetA > 0) tipoA = 1; // Haberes c/ret
                      else if (habSRetA > 0) tipoA = 2; // Haberes s/ret
                      else if (retA > 0) tipoA = 3; // Retenciones
                      
                      // Determinar el tipo de concepto para B
                      let tipoB = 3; // Por defecto, otros
                      if (habCRetB > 0) tipoB = 1; // Haberes c/ret
                      else if (habSRetB > 0) tipoB = 2; // Haberes s/ret
                      else if (retB > 0) tipoB = 3; // Retenciones
                      
                      return tipoA - tipoB;
                    })
                    .map((r, idx) => (
                      <tr key={idx}>
                        <td style={{ textAlign: 'left' }}>{r.ConcDescr}</td>
                        <td style={{ textAlign: 'right' }}>{r.ConcCant}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(r.UnCant)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(r.ConcImpHabCRet)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(r.ConcImpHabSRet)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(r.ConcImpRet)}</td>
                      </tr>
                    ))}
                  {/* Subtotales por columna */}
                  {(() => {
                    const conceptos = detalle.registros.filter(r => {
                      const habCRet = parseFloat(r.ConcImpHabCRet) || 0;
                      const habSRet = parseFloat(r.ConcImpHabSRet) || 0;
                      const ret = parseFloat(r.ConcImpRet) || 0;
                      return habCRet !== 0 || habSRet !== 0 || ret !== 0;
                    });
                    const subtotalHabCRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabCRet) || 0), 0);
                    const subtotalHabSRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabSRet) || 0), 0);
                    const subtotalRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpRet) || 0), 0);
                    return (
                      <tr style={{ background: '#f5f5f5', fontWeight: 'bold' }}>
                        <td colSpan={3} style={{ textAlign: 'right' }}></td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(subtotalHabCRet)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(subtotalHabSRet)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(subtotalRet)}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
              {/* Pie de recibo */}
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2">Lugar y fecha de pago: {detalle.registros[0].LugarPago} - {formatFechaSegura(detalle.registros[0].FechaPago)}</Typography>
                {(() => {
                  const conceptos = detalle.registros.filter(r => {
                    const habCRet = parseFloat(r.ConcImpHabCRet) || 0;
                    const habSRet = parseFloat(r.ConcImpHabSRet) || 0;
                    const ret = parseFloat(r.ConcImpRet) || 0;
                    return habCRet !== 0 || habSRet !== 0 || ret !== 0;
                  });
                  const subtotalHabCRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabCRet) || 0), 0);
                  const subtotalHabSRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpHabSRet) || 0), 0);
                  const subtotalRet = conceptos.reduce((acc, r) => acc + (parseFloat(r.ConcImpRet) || 0), 0);
                  const neto = subtotalHabCRet + subtotalHabSRet - subtotalRet;
                  return (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Neto a cobrar: <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{formatNumber(neto)}</span>
                    </Typography>
                  );
                })()}
                <Typography variant="body2" sx={{ mt: 1 }}>Recibo en conformidad del importe de la presente liquidación en pago de mi remuneración correspondiente al período arriba indicado y copia de este recibo.</Typography>
              </Box>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button variant="contained" onClick={handleCloseDetalle}>Cerrar</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Modal para firmar recibo */}
      <Modal open={firmaModal.open} onClose={() => setFirmaModal({ open: false, periodo: null })}>
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          bgcolor: 'background.paper', 
          p: isMobile ? 2 : 4, 
          borderRadius: 2, 
          boxShadow: 24, 
          width: isMobile ? '90vw' : 350 
        }}>
          <Typography variant="h6" gutterBottom>Firmar Recibo</Typography>
          <Typography variant="body2" gutterBottom>
            Para firmar el recibo del período <b>
              {(() => {
                if (firmaModal.periodo && firmaModal.periodo.includes('_')) {
                  const [periodo, tipo] = firmaModal.periodo.split('_');
                  const tipoTexto = tipo === 'sac' ? 'SAC (Aguinaldo)' : 'Mensual';
                  return `${periodo} (${tipoTexto})`;
                }
                return firmaModal.periodo;
              })()}
            </b> ingresa tu contraseña:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            “Declaro haber recibido y aceptado el presente recibo de haberes. La aceptación mediante usuario y contraseña constituye mi firma electrónica conforme Ley 25.506.”
          </Typography>
          <TextField
            label="Contraseña"
            type="password"
            value={firmaPassword}
            onChange={e => setFirmaPassword(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
            error={!!firmaError}
            helperText={firmaError}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setFirmaModal({ open: false, periodo: null })}>Cancelar</Button>
            <Button variant="contained" color="success" onClick={handleFirmaSubmit}>Firmar</Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Historial de Importaciones */}
      <HistorialImportaciones 
        open={showHistory} 
        onClose={() => setShowHistory(false)} 
      />

      {/* Modal de Gestión de Empresas */}
      <Modal open={showEmpresas} onClose={() => setShowEmpresas(false)}>
        <Box sx={{ 
          position: 'absolute', 
          top: '5%', 
          left: '5%', 
          right: '5%', 
          bottom: '5%', 
          bgcolor: 'background.paper', 
          borderRadius: 2, 
          boxShadow: 24, 
          overflow: 'auto'
        }}>
          <GestionEmpresas />
        </Box>
      </Modal>

      {/* Modal de Carga de Pago de Aportes (nuevo formato de recibo) */}
      <Modal open={showPagosAportes} onClose={() => setShowPagosAportes(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 600 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4
        }}>
          <Typography variant="h6" gutterBottom>Cargar Pago de Aportes</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            El recibo nuevo (desde el período 09/2026) muestra el depósito de los aportes del período
            anterior: banco ("Lugar de pago") y fecha ("F. Pago Aportes"). Cargá acá, mes a mes, por
            empresa, cuándo y dónde se pagaron. Ej.: en el recibo de septiembre se informa el pago de agosto.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Empresa"
                value={pagoAportesEmpresa}
                onChange={(e) => setPagoAportesEmpresa(e.target.value)}
                fullWidth
                size="small"
              >
                {empresasActivas.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.nombre}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                label="Período que se paga"
                value={pagoAportesMes}
                onChange={(e) => setPagoAportesMes(e.target.value)}
                fullWidth
                size="small"
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Año"
                type="number"
                value={pagoAportesAnio}
                onChange={(e) => setPagoAportesAnio(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 2000, max: 2100 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Fecha de pago"
                type="date"
                value={pagoAportesFecha}
                onChange={(e) => setPagoAportesFecha(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Banco (lugar de pago)"
                value={pagoAportesBanco}
                onChange={(e) => setPagoAportesBanco(e.target.value.toUpperCase())}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
          <Button
            variant="contained"
            onClick={handleGuardarPagoAportes}
            disabled={guardandoPagoAportes}
            sx={{ mb: 3 }}
          >
            {guardandoPagoAportes ? 'Guardando...' : 'Guardar'}
          </Button>

          <Typography variant="subtitle2" gutterBottom>Ya cargados</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Fecha de pago</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagosAportes.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">Sin pagos cargados todavía</TableCell></TableRow>
                )}
                {pagosAportes.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.empresa_nombre || p.empresa_id}</TableCell>
                    <TableCell>{p.periodo}</TableCell>
                    <TableCell>{String(p.fecha_pago || '').slice(0, 10).split('-').reverse().join('/')}</TableCell>
                    <TableCell>{p.banco}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleEliminarPagoAportes(p.id)}>
                        🗑️
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowPagosAportes(false)}>Cerrar</Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Información sobre el Proceso de Firma */}
      <Modal 
        open={mostrarModalInfo} 
        onClose={() => setMostrarModalInfo(false)}
        aria-labelledby="modal-info-titulo"
      >
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper', 
          borderRadius: 2, 
          boxShadow: 24, 
          p: 4
        }}>
          <Typography id="modal-info-titulo" variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚠️ Importante
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
            <Typography variant="body1">
              Para ver y acceder a tu <strong>último recibo</strong>, primero debes firmar todos los recibos anteriores pendientes.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={() => setMostrarModalInfo(false)}
              sx={{ px: 4 }}
            >
              Entendido
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Recibos;
